import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import api from '../../services/api';
import { useProject } from '../../context/ProjectContext';
import { Modal } from '../Common/Modal';
import { ConfirmDialog } from '../Common/ConfirmDialog';
import { DataGrid } from '../Common/DataGrid';
import Spinner from '../Common/Spinner';
import Alert from '../Common/Alert';
import { crf as crfMessages, common as commonMessages } from '../../utils/validationMessages';
import { withBasePath } from '../../utils/pathUtils';
import stageService from '../../services/stageService';
import { resolveFirstReviewStageCode } from '../../utils/stageReviewTierUtils';
import './CRFManagement.css';

const CRFManagement = () => {
  const { currentProject } = useProject();
  const [profilePermissions, setProfilePermissions] = useState({});
  const isViewerProfile = profilePermissions.isViewOnly === 1 || profilePermissions.IsViewOnly === 1;
  const [activeTab, setActiveTab] = useState('groups');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // State for each tab
  const [groups, setGroups] = useState([]);
  const [subGroups, setSubGroups] = useState([]);
  const [fields, setFields] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedSubGroup, setSelectedSubGroup] = useState('');
  const [availableFields, setAvailableFields] = useState([]);
  const [selectedFields, setSelectedFields] = useState([]);
  const [templateName, setTemplateName] = useState('');

  // Modals
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showSubGroupModal, setShowSubGroupModal] = useState(false);
  const [showFieldModal, setShowFieldModal] = useState(false);
  const [showTemplateEditModal, setShowTemplateEditModal] = useState(false);
  const [showTemplateMappingModal, setShowTemplateMappingModal] = useState(false);
  const [showTemplateFieldsModal, setShowTemplateFieldsModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateFields, setTemplateFields] = useState([]);
  const [availableFieldsForTemplate, setAvailableFieldsForTemplate] = useState([]);
  const [selectedFieldsForTemplate, setSelectedFieldsForTemplate] = useState([]);
  const [selectedGroupForTemplate, setSelectedGroupForTemplate] = useState('');
  const [selectedSubGroupForTemplate, setSelectedSubGroupForTemplate] = useState('');
  const [projects, setProjects] = useState([]);
  const [mappingForm, setMappingForm] = useState({
    templateId: null,
    projectCode: '',
    visitType: ''
  });

  // Show Mapping modal: assigned Project + Visit Type for a template
  const [showMappingListModal, setShowMappingListModal] = useState(false);
  const [mappingListTemplate, setMappingListTemplate] = useState(null);
  const [templateMappings, setTemplateMappings] = useState([]);
  const [loadingMappings, setLoadingMappings] = useState(false);

  // Drag-and-drop in CRF Template Design (Available <-> Selected)
  const [dragOverZone, setDragOverZone] = useState(null);

  // Field-level validation errors for modals
  const [groupFormErrors, setGroupFormErrors] = useState({});
  const [subGroupFormErrors, setSubGroupFormErrors] = useState({});
  const [fieldFormErrors, setFieldFormErrors] = useState({});
  // Validation errors for CRF Template Design (Template Name, Report Group, Report Sub Group)
  const [designFormErrors, setDesignFormErrors] = useState({});
  // Validation errors for Map Template to Visit modal (Project, Visit Type)
  const [mappingFormErrors, setMappingFormErrors] = useState({});
  // Validation errors for Edit Fields modal filters (Filter by Report Group, Filter by Report Sub Group)
  const [templateFieldsFilterErrors, setTemplateFieldsFilterErrors] = useState({});

  // Custom confirm dialog (replaces window.confirm)
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null,
    confirmText: 'Yes',
    cancelText: 'No',
    variant: 'danger',
  });
  const [toastMessage, setToastMessage] = useState(null); // { type: 'success'|'error', message: string }

  /** Returns true if another item (different code) has the same description (case-insensitive, trimmed). */
  const isDuplicateDescription = (list, description, currentCode) => {
    if (!list || !Array.isArray(list) || !(description ?? '').toString().trim()) return false;
    const descLower = (description ?? '').toString().trim().toLowerCase();
    return list.some((item) => {
      const code = item?.code ?? item?.Code ?? item?.id;
      if (currentCode != null && currentCode !== undefined && String(code) === String(currentCode)) return false;
      const d = (item?.description ?? item?.Description ?? '').toString().trim().toLowerCase();
      return d === descLower;
    });
  };

  useEffect(() => {
    // Load initial data
    loadGroups();
    loadSubGroups();
    loadFields();
    loadTemplates();
    loadProjects();
  }, []);

  useEffect(() => {
    let cancelled = false;
    api.get('/users/me/profile-permissions')
      .then((res) => {
        if (!cancelled && res.data) setProfilePermissions(res.data);
      })
      .catch(() => {
        if (!cancelled) setProfilePermissions({});
      });
    return () => { cancelled = true; };
  }, [currentProject]);

  // When user switches sub-modules (tabs), clear that tab's previous activity so coming back shows a fresh state
  useEffect(() => {
    switch (activeTab) {
      case 'groups':
        setEditingItem(null);
        setShowGroupModal(false);
        setGroupFormErrors({});
        break;
      case 'subgroups':
        setEditingItem(null);
        setShowSubGroupModal(false);
        setSubGroupFormErrors({});
        break;
      case 'fields':
        setEditingItem(null);
        setShowFieldModal(false);
        setFieldFormErrors({});
        break;
      case 'design':
        setTemplateName('');
        setSelectedGroup('');
        setSelectedSubGroup('');
        setAvailableFields([]);
        setSelectedFields([]);
        setDesignFormErrors({});
        setDragOverZone(null);
        break;
      case 'templates':
        setShowTemplateEditModal(false);
        setShowTemplateMappingModal(false);
        setShowTemplateFieldsModal(false);
        setShowMappingListModal(false);
        setEditingTemplate(null);
        setMappingForm({ templateId: null, projectCode: '', visitType: '' });
        setMappingFormErrors({});
        setTemplateFieldsFilterErrors({});
        break;
      default:
        break;
    }
  }, [activeTab]);

  // When Map Template to Visit modal is open, keep Project dropdown in sync with session (handles projects loading after modal open)
  useEffect(() => {
    if (!showTemplateMappingModal || !currentProject || currentProject === 'all' || !Array.isArray(projects) || projects.length === 0) return;
    const normalize = (s) => String(s || '').trim().toLowerCase().replace(/^0+/, '') || '';
    const sessionNorm = normalize(currentProject);
    const match = projects.find((p) => {
      const code = (p.projectCode || '').toString().trim();
      return code === currentProject || normalize(code) === sessionNorm;
    });
    if (match) {
      setMappingForm((prev) => (prev.projectCode === match.projectCode ? prev : { ...prev, projectCode: match.projectCode }));
    }
  }, [showTemplateMappingModal, currentProject, projects]);

  // When Add/Edit CRF Field modal opens, bind all groups and sub groups again (refresh dropdowns)
  useEffect(() => {
    if (!showFieldModal) return;
    const bind = async () => {
      try {
        const [grRes, sgRes] = await Promise.all([
          api.get('/crftemplate/groups', { params: { includeInactive: true } }),
          api.get('/crftemplate/subgroups', { params: { includeInactive: true } }),
        ]);
        setGroups(grRes.data || []);
        setSubGroups(sgRes.data || []);
      } catch (err) {
        console.error('Error refreshing groups/subgroups:', err);
      }
    };
    bind();
  }, [showFieldModal]);

  const loadGroups = async () => {
    try {
      setLoading(true);
      const response = await api.get('/crftemplate/groups', { params: { includeInactive: true } });
      setGroups(response.data || []);
    } catch (err) {
      console.error('Error loading groups:', err);
      setError('Failed to load groups');
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  const loadSubGroups = async () => {
    try {
      const response = await api.get('/crftemplate/subgroups', { params: { includeInactive: true } });
      setSubGroups(response.data || []);
    } catch (err) {
      console.error('Error loading sub groups:', err);
      setError('Failed to load sub groups');
      setSubGroups([]);
    }
  };

  const loadFields = async () => {
    try {
      const response = await api.get('/crftemplate/fields', { params: { includeInactive: true } });
      setFields(response.data || []);
    } catch (err) {
      console.error('Error loading fields:', err);
      setError('Failed to load fields');
      setFields([]);
    }
  };

  /** Resolve report group / sub group labels for search (matches visible table cells, not only raw codes). */
  const fieldsWithReportSearchMeta = useMemo(() => {
    const list = Array.isArray(fields) ? fields : [];
    return list.map((field) => {
      const gCode = field.groupCode || field.GroupCode;
      const sgCode = field.subGroupCode || field.SubGroupCode;
      const group = groups.find((g) => (g.code || g.Code) === gCode);
      const subGroup = subGroups.find((sg) => (sg.code || sg.Code) === sgCode);
      const groupBits = [gCode, group?.description, group?.Description].filter(Boolean);
      const subBits = [sgCode, subGroup?.description, subGroup?.Description].filter(Boolean);
      return {
        ...field,
        __searchReportGroup: groupBits.join(' '),
        __searchReportSubGroup: subBits.join(' '),
      };
    });
  }, [fields, groups, subGroups]);

  const loadTemplates = async () => {
    try {
      const response = await api.get('/crftemplate/templates');
      setTemplates(response.data || []);
    } catch (err) {
      console.error('Error loading templates:', err);
      setError('Failed to load templates');
      setTemplates([]);
    }
  };

  const loadProjects = async () => {
    try {
      // Only projects the current user has in UserRoleProjectSiteMapping (for Map Template to Visit dropdown)
      const response = await api.get('/ImportData/GetProjectsForCurrentUser');
      const mappedProjects = (response.data || []).map(project => ({
        projectCode: project.projectCode || project.ProjectCode,
        projectName: project.projectName || project.ProjectDescription || project.ProjectName || project.projectCode || project.ProjectCode
      }));
      setProjects(mappedProjects);
    } catch (err) {
      console.error('Error fetching projects for current user:', err);
      setProjects([]);
    }
  };

  const handleAddField = (field) => {
    const fieldId = field.id || field.code;
    if (!selectedFields.find(f => (f.id || f.code) === fieldId)) {
      setSelectedFields([...selectedFields, field]);
      setAvailableFields(prev => prev.filter(f => (f.id || f.code) !== fieldId));
    }
  };

  const handleRemoveField = (fieldId) => {
    const removedField = selectedFields.find(f => (f.id || f.code) === fieldId);
    setSelectedFields(selectedFields.filter(f => (f.id || f.code) !== fieldId));
    if (removedField) {
      const matchesFilter = (!selectedGroup || (removedField.groupCode || removedField.GroupCode) === selectedGroup) &&
        (!selectedSubGroup || (removedField.subGroupCode || removedField.SubGroupCode) === selectedSubGroup);
      if (matchesFilter) {
        setAvailableFields(prev => {
          if (prev.some(f => (f.id || f.code) === fieldId)) return prev;
          return [...prev, removedField];
        });
      }
    }
  };

  /** Reorder Selected Fields: move item with draggedId to position of dropTargetId (or to end if dropTargetId is null). */
  const reorderSelectedFields = (draggedId, dropTargetId) => {
    const list = [...selectedFields];
    const fromIndex = list.findIndex(f => (f.id || f.code) === draggedId);
    if (fromIndex === -1) return;
    const [removed] = list.splice(fromIndex, 1);
    if (dropTargetId == null) {
      list.push(removed);
    } else {
      let toIndex = list.findIndex(f => (f.id || f.code) === dropTargetId);
      if (toIndex === -1) {
        list.push(removed);
        return;
      }
      if (fromIndex < toIndex) toIndex -= 1;
      list.splice(toIndex, 0, removed);
    }
    setSelectedFields(list);
  };

  const handleSaveGroup = async (formData) => {
    try {
      setLoading(true);
      // Map active (boolean) to ActiveFlag format and ensure code is set
      const payload = {
        code: formData.code,
        description: formData.description,
        remarks: formData.remarks || '',
        active: formData.active !== undefined ? formData.active : true
      };
      
      if (editingItem) {
        // Use code from editingItem (which should be the same as formData.code)
        const code = editingItem.code || editingItem.id;
        await api.post(`/crftemplate/groups/${code}`, payload);
        await loadGroups();
      } else {
        await api.post('/crftemplate/groups', payload);
        await loadGroups();
      }
      setToastMessage({ type: 'success', message: commonMessages.authSuccess });
      setShowGroupModal(false);
      setEditingItem(null);
      setGroupFormErrors({});
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to save group';
      setToastMessage({ type: 'error', message: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSubGroup = async (formData) => {
    try {
      setLoading(true);
      // Map active (boolean) to ActiveFlag format
      const payload = {
        code: formData.code,
        description: formData.description,
        groupCode: formData.groupCode || null,
        remark: (formData.remarks || '').trim() || null,
        active: formData.active !== undefined ? formData.active : true
      };
      // Use code from captured payload first (reliable after confirm), then editingItem
      const code = formData?.code ?? editingItem?.code ?? editingItem?.Code ?? editingItem?.id;
      if (code) {
        await api.post(`/crftemplate/subgroups/${code}`, payload);
        await loadSubGroups();
      } else {
        await api.post('/crftemplate/subgroups', payload);
        await loadSubGroups();
      }
      setToastMessage({ type: 'success', message: commonMessages.authSuccess });
      setShowSubGroupModal(false);
      setEditingItem(null);
      setSubGroupFormErrors({});
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to save sub group';
      setToastMessage({ type: 'error', message: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveField = async (formData) => {
    try {
      setLoading(true);
      // Map active (boolean) to ActiveFlag format
      const payload = {
        code: formData.code,
        description: formData.description,
        groupCode: formData.groupCode || null,
        subGroupCode: formData.subGroupCode || null,
        type: formData.type || 'TextBox',
        validationType: formData.validationType || 'NA',
        defaultValue: formData.defaultValue || null,
        uom: formData.uom || null,
        alertOnValue: formData.alertOnValue || null,
        alertMessage: formData.alertMessage || null,
        values: formData.values || null,
        active: formData.active !== undefined ? formData.active : true,
        notNull: formData.notNull || false,
        sequence: formData.sequence ?? null,
        remark: formData.remark || null,
        visitReportFillHint: (formData.visitReportFillHint || '').trim(),
        refType: formData.refType || null,
        refTable: formData.refTable || null,
        refColumn: formData.refColumn || null,
        refFilePath: formData.refFilePath || null,
        alertType: formData.alertType || null,
        cdiscValues: formData.cdiscValues || null,
        otherValues: formData.otherValues || null,
        currentDate: formData.currentDate || null,
        formula: formData.formula || null,
      };
      
      if (editingItem) {
        // Use code from editingItem
        const code = editingItem.code || editingItem.id;
        await api.post(`/crftemplate/fields/${code}`, payload);
        await loadFields();
      } else {
        await api.post('/crftemplate/fields', payload);
        await loadFields();
      }
      setToastMessage({ type: 'success', message: commonMessages.authSuccess });
      setShowFieldModal(false);
      setEditingItem(null);
      setFieldFormErrors({});
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to save field';
      setToastMessage({ type: 'error', message: errorMessage });
      if (err.response?.status === 400) {
        const apiErr = err.response?.data?.error || errorMessage;
        if (typeof apiErr === 'string' && apiErr.toLowerCase().includes('report field suggestion')) {
          setFieldFormErrors({ visitReportFillHint: apiErr });
        } else {
          setFieldFormErrors({ description: apiErr });
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const validateDesignForm = () => {
    const errs = {};
    if (!(templateName || '').trim()) errs.templateName = crfMessages.templateNameRequired;
    if (!(selectedGroup || '').trim()) errs.selectedGroup = crfMessages.attributeGroupSelect;
    if (!(selectedSubGroup || '').trim()) errs.selectedSubGroup = crfMessages.attributeSubGroupSelect;
    if (!selectedFields || selectedFields.length === 0) errs.selectedFields = crfMessages.selectOneAttribute;
    setDesignFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSaveTemplate = async () => {
    if (!(templateName || '').trim()) {
      setDesignFormErrors((e) => ({ ...e, templateName: crfMessages.templateNameRequired }));
      return;
    }
    if (!(selectedGroup || '').trim()) {
      setDesignFormErrors((e) => ({ ...e, selectedGroup: crfMessages.attributeGroupSelect }));
      return;
    }
    if (!(selectedSubGroup || '').trim()) {
      setDesignFormErrors((e) => ({ ...e, selectedSubGroup: crfMessages.attributeSubGroupSelect }));
      return;
    }
    if (!selectedFields || selectedFields.length === 0) {
      setDesignFormErrors((e) => ({ ...e, selectedFields: crfMessages.selectOneAttribute }));
      return;
    }
    setDesignFormErrors({});

    try {
      setLoading(true);
      // Backend generates TemplateId as {group}-{subgroup}-{nMedExTemplateHdrNo}
      const response = await api.post('/crftemplate/templates', {
        templateName: templateName.trim(),
        groupCode: (selectedGroup || '').trim(),
        subGroupCode: (selectedSubGroup || '').trim()
      });
      
      const createdTemplateId = response.data?.templateId || response.data?.TemplateId;
      if (createdTemplateId && selectedFields.length > 0) {
        try {
          const codesToAdd = selectedFields.map(f => f.code || f.Code || f.id).filter(Boolean);
          await api.post(`/crftemplate/templates/${createdTemplateId}/fields/batch`, codesToAdd);
        } catch (detailErr) {
          const msg = detailErr.response?.data?.error || detailErr.response?.data?.message || detailErr.message;
          console.error('Failed to add fields to template:', detailErr);
          setToastMessage({ type: 'info', message: msg || 'Template created but some fields could not be added. You can add them via Edit Fields.' });
        }
      }

      setToastMessage({ type: 'success', message: commonMessages.authSuccess });
      setDesignFormErrors({});
      setTemplateName('');
      setSelectedFields([]);
      setSelectedGroup('');
      setSelectedSubGroup('');
      setAvailableFields([]);
      loadTemplates();
    } catch (err) {
      const errorMessage = err.response?.data?.error || 
                          err.response?.data?.message || 
                          err.message || 
                          'Failed to save template';
      // Only show toast (top right); do not set error so the inline banner does not appear in CRF Template Design
      setToastMessage({ type: 'error', message: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewTemplate = async (template) => {
    const templateId = template?.templateId || template?.TemplateId || template?.id || template?.templateHeaderNo;
    const templateDisplayName = (
      template?.templateName ||
      template?.TemplateName ||
      template?.name ||
      ''
    ).toString().trim();
    if (!templateId) {
      setToastMessage({ type: 'error', message: 'Template ID is missing. Cannot preview template.' });
      return;
    }
    try {
      const res = await api.get(`/crftemplate/templates/${templateId}/fields`);
      const fieldList = res.data || [];
      if (!Array.isArray(fieldList) || fieldList.length === 0) {
        setToastMessage({ type: 'error', message: 'This template has no fields. Add fields below, then try Preview again.' });
        await handleEditTemplateFields(template);
        return;
      }
      const previewParams = new URLSearchParams({ preview: '1' });
      if (templateDisplayName) previewParams.set('reportName', templateDisplayName);
      window.open(withBasePath(`/crf/template/${templateId}?${previewParams.toString()}`), '_blank');
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Could not load template.';
      setToastMessage({ type: 'error', message: msg });
    }
  };

  const handleCopyTemplate = async (template) => {
    const templateId = template.templateId || template.TemplateId || template.id || template.templateHeaderNo;
    try {
      setLoading(true);
      const res = await api.post(`/crftemplate/templates/${templateId}/copy`);
      const created = res.data;
      await loadTemplates();
      const newId = created?.templateId || created?.TemplateId;
      setToastMessage({ type: 'success', message: commonMessages.authSuccess });
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to copy template';
      setToastMessage({ type: 'error', message: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTemplateEdit = async (formData) => {
    try {
      setLoading(true);
      // Use templateId (not id) for update - get from formData or editingItem
      // Handle both camelCase and PascalCase
      const templateId = formData.templateId || 
                        editingItem?.templateId || 
                        editingItem?.TemplateId || 
                        editingItem?.id ||
                        editingItem?.templateHeaderNo;
      
      if (!templateId) {
        setToastMessage({ type: 'error', message: 'Template ID is required. Cannot update template.' });
        setLoading(false);
        return;
      }
      
      // Map form data to backend model format (camelCase for JSON serialization)
      const payload = {
        templateName: formData.templateName || formData.name || '',
        templateId: templateId,
        isActive: formData.isActive !== undefined ? formData.isActive : (editingItem?.isActive !== false && editingItem?.IsActive !== false),
        description: formData.description ?? editingItem?.description ?? editingItem?.Description ?? '',
        recordedBy: editingItem?.recordedBy || editingItem?.RecordedBy || 1,
        recordedAtTimeZone: editingItem?.recordedAtTimeZone || editingItem?.RecordedAtTimeZone || 91,
        recordedAtOffSet: editingItem?.recordedAtOffSet || editingItem?.RecordedAtOffSet || '+05:30',
        recordedSign: editingItem?.recordedSign || editingItem?.RecordedSign || 'System'
      };
      
      if (!payload.templateName) {
        setToastMessage({ type: 'error', message: 'Template Name is required' });
        setLoading(false);
        return;
      }

      await api.post(`/crftemplate/templates/${templateId}`, payload);
      await loadTemplates();
      setToastMessage({ type: 'success', message: commonMessages.authSuccess });
      setShowTemplateEditModal(false);
      setEditingItem(null);
    } catch (err) {
      const errorMessage = err.response?.data?.error || 
                          err.response?.data?.message || 
                          err.message || 
                          'Failed to update template';
      setError(errorMessage);
      setToastMessage({ type: 'error', message: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handleMapTemplate = (template) => {
    const isActive = template?.isActive !== false && template?.IsActive !== false;
    if (!isActive) {
      setToastMessage({ type: 'error', message: crfMessages.inactiveTemplateCannotMap });
      return;
    }
    const templateId = template.templateId || template.TemplateId || template.id || template.templateHeaderNo;
    const sessionProject = currentProject && currentProject !== 'all' ? currentProject : '';
    // Resolve to the projectCode from dropdown options so the select shows session project (e.g. 198-23 -> 0198-23)
    let projectCodeToSet = sessionProject;
    if (sessionProject && Array.isArray(projects) && projects.length > 0) {
      const normalize = (s) => String(s || '').trim().toLowerCase().replace(/^0+/, '') || '';
      const match = projects.find((p) => {
        const code = (p.projectCode || '').toString().trim();
        return code === sessionProject || normalize(code) === normalize(sessionProject);
      });
      if (match) projectCodeToSet = match.projectCode;
    }
    setMappingForm({
      templateId: templateId,
      projectCode: projectCodeToSet,
      visitType: ''
    });
    setShowTemplateMappingModal(true);
  };

  const handleShowMapping = async (template) => {
    const templateId = template.templateId || template.TemplateId || template.id || template.templateHeaderNo;
    setMappingListTemplate(template);
    setShowMappingListModal(true);
    setTemplateMappings([]);
    setLoadingMappings(true);
    try {
      const res = await api.get(`/crftemplate/templates/${templateId}/mappings`);
      setTemplateMappings(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error loading template mappings:', err);
      setTemplateMappings([]);
      setToastMessage({ type: 'error', message: err.response?.data?.error || err.message || 'Failed to load mappings.' });
    } finally {
      setLoadingMappings(false);
    }
  };

  const handleRemoveMapping = async (assignmentId) => {
    if (!mappingListTemplate) return;
    const templateId = mappingListTemplate.templateId || mappingListTemplate.TemplateId || mappingListTemplate.id || mappingListTemplate.templateHeaderNo;
    try {
      setLoadingMappings(true);
      await api.post(`/crftemplate/template-mappings/${assignmentId}/remove`);
      setToastMessage({ type: 'success', message: commonMessages.authSuccess });
      setShowMappingListModal(false);
      setMappingListTemplate(null);
      setTemplateMappings([]);
    } catch (err) {
      setToastMessage({ type: 'error', message: err.response?.data?.error || err.response?.data?.message || crfMessages.cannotRemoveMapping || err.message || 'Failed to remove assignment.' });
    } finally {
      setLoadingMappings(false);
    }
  };

  const checkTemplateCanEdit = async (template) => {
    const templateId = template?.templateId || template?.TemplateId || template?.id || template?.templateHeaderNo;
    if (!templateId) {
      setToastMessage({ type: 'error', message: crfMessages.onlyDraftEditable });
      return false;
    }
    try {
      const res = await api.get(`/crftemplate/templates/${encodeURIComponent(templateId)}/can-edit`);
      const canEdit = res.data?.canEdit === true;
      if (!canEdit) {
        const msg = res.data?.message || crfMessages.templateInUse;
        setToastMessage({ type: 'error', message: msg });
        return false;
      }
      return true;
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Could not verify template.';
      setToastMessage({ type: 'error', message: msg });
      return false;
    }
  };

  const handleDeleteTemplate = (template) => {
    const templateId = template?.templateId || template?.TemplateId || template?.id || template?.templateHeaderNo;
    if (!templateId) return;
    setConfirmDialog({
      open: true,
      title: 'Delete Template',
      message: crfMessages.deleteConfirm + ' Yes/No',
      confirmText: 'Yes',
      cancelText: 'No',
      variant: 'danger',
      onConfirm: async () => {
        try {
          setLoading(true);
          await api.post(`/crftemplate/templates/${encodeURIComponent(templateId)}`);
          await loadTemplates();
          setToastMessage({ type: 'success', message: commonMessages.authSuccess });
        } catch (err) {
          const msg = err.response?.data?.error || err.response?.data?.message || crfMessages.deleteInUse || err.message || 'Failed to delete template.';
          setToastMessage({ type: 'error', message: msg });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleEditTemplateFields = async (template) => {
    const canEdit = await checkTemplateCanEdit(template);
    if (!canEdit) return;
    try {
      setLoading(true);
      const templateId = template.templateId || template.TemplateId || template.id || template.templateHeaderNo;
      setEditingTemplate(template);
      
      // Reset filter selections
      setSelectedGroupForTemplate('');
      setSelectedSubGroupForTemplate('');
      
      // Load existing template fields
      const response = await api.get(`/crftemplate/templates/${templateId}/fields`);
      const existingFields = response.data || [];
      setTemplateFields(existingFields);
      
      // Get field codes from existing template fields
      const existingFieldCodes = existingFields.map(f => f.fieldCode || f.FieldCode).filter(Boolean);
      setSelectedFieldsForTemplate(existingFields.map(f => ({
        code: f.fieldCode || f.FieldCode,
        description: f.fieldDescription || f.FieldDescription,
        id: f.fieldCode || f.FieldCode,
        groupCode: f.groupCode || f.GroupCode,
        subGroupCode: f.subGroupCode || f.SubGroupCode
      })));
      
      // Load all available fields and filter out already selected ones (active only)
      await loadFields();
      const allFields = fields.filter(f => {
        if (f.active === false) return false;
        const fieldCode = f.code || f.Code;
        return !existingFieldCodes.includes(fieldCode);
      });
      setAvailableFieldsForTemplate(allFields);
      
      setShowTemplateFieldsModal(true);
    } catch (err) {
      console.error('Error loading template fields:', err);
      setToastMessage({ type: 'error', message: 'Failed to load template fields' });
    } finally {
      setLoading(false);
    }
  };

  const handleGroupChangeForTemplate = (groupCode) => {
    setSelectedGroupForTemplate(groupCode);
    setSelectedSubGroupForTemplate('');
    setTemplateFieldsFilterErrors(prev => {
      const next = { ...prev };
      delete next.selectedGroup;
      delete next.selectedSubGroup;
      return next;
    });
    
    // Filter available fields by group (active only)
    const existingFieldCodes = templateFields.map(f => f.fieldCode || f.FieldCode).filter(Boolean);
    let filteredFields = fields.filter(f => {
      if (f.active === false) return false;
      const fieldCode = f.code || f.Code;
      return !existingFieldCodes.includes(fieldCode);
    });
    
    if (groupCode) {
      filteredFields = filteredFields.filter(f => 
        (f.groupCode || f.GroupCode) === groupCode
      );
    }
    
    setAvailableFieldsForTemplate(filteredFields);
  };

  const handleSubGroupChangeForTemplate = (subGroupCode) => {
    setSelectedSubGroupForTemplate(subGroupCode);
    setTemplateFieldsFilterErrors(prev => {
      const next = { ...prev };
      delete next.selectedSubGroup;
      return next;
    });
    
    // Filter available fields by group and sub group (active only)
    const existingFieldCodes = templateFields.map(f => f.fieldCode || f.FieldCode).filter(Boolean);
    let filteredFields = fields.filter(f => {
      if (f.active === false) return false;
      const fieldCode = f.code || f.Code;
      return !existingFieldCodes.includes(fieldCode);
    });
    
    if (selectedGroupForTemplate) {
      filteredFields = filteredFields.filter(f => 
        (f.groupCode || f.GroupCode) === selectedGroupForTemplate
      );
    }
    
    if (subGroupCode) {
      filteredFields = filteredFields.filter(f => 
        (f.subGroupCode || f.SubGroupCode) === subGroupCode
      );
    }
    
    setAvailableFieldsForTemplate(filteredFields);
  };

  const handleAddFieldToTemplate = (field) => {
    const fieldId = field.id || field.code;
    if (!selectedFieldsForTemplate.find(f => (f.id || f.code) === fieldId)) {
      setSelectedFieldsForTemplate([...selectedFieldsForTemplate, field]);
      // Remove from available fields
      const updatedAvailable = availableFieldsForTemplate.filter(f => (f.id || f.code) !== fieldId);
      setAvailableFieldsForTemplate(updatedAvailable);
    }
  };

  const handleRemoveFieldFromTemplate = (fieldId) => {
    const removedField = selectedFieldsForTemplate.find(f => (f.id || f.code) === fieldId);
    if (removedField) {
      setSelectedFieldsForTemplate(selectedFieldsForTemplate.filter(f => (f.id || f.code) !== fieldId));
      
      // Add back to available fields if it matches current filter
      const field = fields.find(f => (f.id || f.code) === fieldId || f.code === removedField.code);
      if (field) {
        // Check if field matches current filter
        let shouldShow = true;
        if (selectedGroupForTemplate) {
          shouldShow = shouldShow && (field.groupCode || field.GroupCode) === selectedGroupForTemplate;
        }
        if (selectedSubGroupForTemplate) {
          shouldShow = shouldShow && (field.subGroupCode || field.SubGroupCode) === selectedSubGroupForTemplate;
        }
        
        // Check if field is not already in template
        const existingFieldCodes = templateFields.map(f => f.fieldCode || f.FieldCode).filter(Boolean);
        const fieldCode = field.code || field.Code;
        shouldShow = shouldShow && !existingFieldCodes.includes(fieldCode);
        
        if (shouldShow && !availableFieldsForTemplate.find(f => (f.id || f.code) === fieldId || f.code === fieldCode)) {
          setAvailableFieldsForTemplate([...availableFieldsForTemplate, field]);
        }
      }
    }
  };

  /** Reorder Template Fields in Edit Fields modal: move item to position of dropTargetId (or to end if null). */
  const reorderSelectedFieldsForTemplate = (draggedId, dropTargetId) => {
    const list = [...selectedFieldsForTemplate];
    const fromIndex = list.findIndex(f => (f.id || f.code) === draggedId);
    if (fromIndex === -1) return;
    const [removed] = list.splice(fromIndex, 1);
    if (dropTargetId == null) {
      list.push(removed);
    } else {
      let toIndex = list.findIndex(f => (f.id || f.code) === dropTargetId);
      if (toIndex === -1) {
        list.push(removed);
        return;
      }
      if (fromIndex < toIndex) toIndex -= 1;
      list.splice(toIndex, 0, removed);
    }
    setSelectedFieldsForTemplate(list);
  };

  const validateTemplateFieldsFilters = () => {
    const errs = {};
    if (!(selectedGroupForTemplate || '').trim()) errs.selectedGroup = 'Please select Report Group.';
    if (!(selectedSubGroupForTemplate || '').trim()) errs.selectedSubGroup = 'Please select Report Sub Group.';
    setTemplateFieldsFilterErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSaveTemplateFields = async () => {
    if (!editingTemplate) return;
    if (!(selectedGroupForTemplate || '').trim() || !(selectedSubGroupForTemplate || '').trim()) {
      setTemplateFieldsFilterErrors({
        selectedGroup: !(selectedGroupForTemplate || '').trim() ? 'Please select Report Group.' : '',
        selectedSubGroup: !(selectedSubGroupForTemplate || '').trim() ? 'Please select Report Sub Group.' : ''
      });
      return;
    }
    setTemplateFieldsFilterErrors({});

    try {
      setLoading(true);
      const templateId = editingTemplate.templateId || editingTemplate.TemplateId || editingTemplate.id || editingTemplate.templateHeaderNo;
      
      // Get current template field codes
      const currentFieldCodes = templateFields.map(f => f.fieldCode || f.FieldCode).filter(Boolean);
      
      // Get new field codes
      const newFieldCodes = selectedFieldsForTemplate.map(f => f.code || f.Code || f.id).filter(Boolean);
      
      // Find fields to add (in new but not in current)
      const fieldsToAdd = newFieldCodes.filter(code => !currentFieldCodes.includes(code));
      
      // Find fields to remove (in current but not in new)
      const fieldsToRemove = templateFields.filter(f => {
        const code = f.fieldCode || f.FieldCode;
        return code && !newFieldCodes.includes(code);
      });

      // Remove fields
      for (const fieldToRemove of fieldsToRemove) {
        const detailNo = fieldToRemove.templateDetailNo || fieldToRemove.TemplateDetailNo;
        if (detailNo) {
          await api.post(`/crftemplate/template-details/${detailNo}`);
        }
      }

      // Add new fields
      if (fieldsToAdd.length > 0) {
        await api.post(`/crftemplate/templates/${templateId}/fields/batch`, fieldsToAdd);
      }

      setToastMessage({ type: 'success', message: commonMessages.authSuccess });
      setShowTemplateFieldsModal(false);
      setEditingTemplate(null);
      setTemplateFields([]);
      setSelectedFieldsForTemplate([]);
      setAvailableFieldsForTemplate([]);
      setSelectedGroupForTemplate('');
      setSelectedSubGroupForTemplate('');
      await loadTemplates();
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to update template fields';
      setError(errorMessage);
      setToastMessage({ type: 'error', message: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handleMappingChange = (field, value) => {
    setMappingForm(prev => ({ ...prev, [field]: value }));
    // Clear that field's error when user changes value
    setMappingFormErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const validateMappingForm = () => {
    const errs = {};
    if (!mappingForm.templateId) errs.templateId = 'Template is required.';
    else {
      const template = templates.find(
        (t) => (t.templateId || t.TemplateId || t.id || t.templateHeaderNo) === mappingForm.templateId
      );
      const isActive = template && template?.isActive !== false && template?.IsActive !== false;
      if (template && !isActive) errs.templateId = crfMessages.inactiveTemplateCannotMap;
    }
    if (!(mappingForm.projectCode || '').trim()) errs.projectCode = 'Please select Project.';
    if (!(mappingForm.visitType || '').trim()) errs.visitType = 'Please select Visit Type.';
    setMappingFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSaveMapping = async () => {
    if (!mappingForm.templateId || !(mappingForm.projectCode || '').trim()) {
      setMappingFormErrors(prev => ({ ...prev, templateId: !mappingForm.templateId ? 'Template is required.' : '', projectCode: !(mappingForm.projectCode || '').trim() ? 'Please select Project.' : '' }));
      return;
    }
    const selectedTemplate = templates.find(
      (t) => (t.templateId || t.TemplateId || t.id || t.templateHeaderNo) === mappingForm.templateId
    );
    const isTemplateActive = selectedTemplate && selectedTemplate?.isActive !== false && selectedTemplate?.IsActive !== false;
    if (selectedTemplate && !isTemplateActive) {
      setMappingFormErrors(prev => ({ ...prev, templateId: crfMessages.inactiveTemplateCannotMap }));
      setToastMessage({ type: 'error', message: crfMessages.inactiveTemplateCannotMap });
      return;
    }
    if (!(mappingForm.visitType || '').trim()) {
      setMappingFormErrors(prev => ({ ...prev, visitType: 'Please select Visit Type.' }));
      return;
    }
    setMappingFormErrors({});

    try {
      setLoading(true);
      await api.post('/crftemplate/template-mappings', {
        templateId: mappingForm.templateId,
        projectCode: mappingForm.projectCode,
        visitType: mappingForm.visitType
      });
      setToastMessage({ type: 'success', message: commonMessages.authSuccess });
      setShowTemplateMappingModal(false);
      setMappingForm({
        templateId: null,
        projectCode: '',
        visitType: ''
      });
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to save template mapping';
      const msg = (errorMessage || '').toLowerCase();
      // Show API message when it asks to remove previous mapping first; same-template conflict uses short message
      if (msg.includes('remove the mapping') || msg.includes('previous template')) {
        setToastMessage({ type: 'error', message: errorMessage });
      } else if (msg.includes('already mapped')) {
        setToastMessage({ type: 'error', message: crfMessages.templateAlreadyMapped });
      } else {
        setToastMessage({ type: 'error', message: errorMessage });
      }
      // Only show toast (top right); do not set error so the banner below header does not appear
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="crf-management">
      

      {error && (
        <div className="alert alert-danger">
          {error}
        </div>
      )}

      <div className="crf-tabs">
        <button
          className={`crf-tab ${activeTab === 'groups' ? 'active' : ''}`}
          onClick={() => setActiveTab('groups')}
        >
          Report Groups
        </button>
        <button
          className={`crf-tab ${activeTab === 'subgroups' ? 'active' : ''}`}
          onClick={() => setActiveTab('subgroups')}
        >
          Report Sub Groups
        </button>
        <button
          className={`crf-tab ${activeTab === 'fields' ? 'active' : ''}`}
          onClick={() => setActiveTab('fields')}
        >
          Report Fields
        </button>
        <button
          className={`crf-tab ${activeTab === 'design' ? 'active' : ''}`}
          onClick={() => setActiveTab('design')}
        >
          Report Template Design
        </button>
        <button
          className={`crf-tab ${activeTab === 'templates' ? 'active' : ''}`}
          onClick={() => setActiveTab('templates')}
        >
          Report Template Management
        </button>
      </div>

      {loading && <Spinner />}

      {/* Report Groups Tab */}
      {activeTab === 'groups' && (
        <div className="crf-tab-content">
          <div className="crf-tab-header" style={{ justifyContent: 'flex-end' }}>
            
            {!isViewerProfile && (
              <button className="btn btn-primary" onClick={() => { setEditingItem(null); setShowGroupModal(true); }}>
                + Add Report Group
              </button>
            )}
          </div>
          <div className="card">
            <DataGrid
              data={groups}
              searchPlaceholder=""
              searchKeys={['description', 'Description', 'remarks', 'Remarks', 'code', 'Code']}
              defaultPageSize={10}
              emptyMessage="No groups found."
              columns={[
                { key: 'description', label: 'Report Group' },
                { key: 'remarks', label: 'Remarks' },
                { key: 'active', label: 'Active' },
                { key: 'action', label: 'Action' },
              ]}
              renderRow={(group) => (
                <tr key={group.id || group.code}>
                  <td>{group.description || group.Description || '-'}</td>
                  <td>{group.remarks || group.Remarks || '-'}</td>
                  <td>
                    <span className={`badge ${(group.active !== undefined ? group.active : (group.activeFlag === 'Y' || group.ActiveFlag === 'Y')) ? 'badge-success' : 'badge-secondary'}`}>
                      {(group.active !== undefined ? group.active : (group.activeFlag === 'Y' || group.ActiveFlag === 'Y')) ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td>
                    {!isViewerProfile && (
                      <button className="btn btn-sm btn-secondary" onClick={() => { setEditingItem(group); setShowGroupModal(true); }}>
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              )}
            />
          </div>
        </div>
      )}

      {/* Report Sub Groups Tab */}
      {activeTab === 'subgroups' && (
        <div className="crf-tab-content">
          <div className="crf-tab-header" style={{ justifyContent: 'flex-end' }}>
            
            {!isViewerProfile && (
              <button className="btn btn-primary" onClick={() => { setEditingItem(null); setShowSubGroupModal(true); }}>
                + Add Report Sub Group
              </button>
            )}
          </div>
          <div className="card">
            <DataGrid
              data={subGroups}
              searchPlaceholder=""
              searchKeys={['description', 'code', 'groupCode', 'remark', 'remarks']}
              defaultPageSize={10}
              emptyMessage="No sub groups found."
              columns={[
                { key: 'description', label: 'Report Sub Group' },
                { key: 'remarks', label: 'Remarks' },
                { key: 'active', label: 'Active' },
                { key: 'action', label: 'Action' },
              ]}
              renderRow={(subGroup) => {
                const remarks = subGroup.remark ?? subGroup.remarks ?? subGroup.Remark ?? subGroup.Remarks ?? '-';
                return (
                  <tr key={subGroup.id || subGroup.code}>
                    <td>{subGroup.description || subGroup.Description || '-'}</td>
                    <td>{remarks}</td>
                    <td>
                      <span className={`badge ${(subGroup.active !== undefined ? subGroup.active : (subGroup.activeFlag === 'Y' || subGroup.ActiveFlag === 'Y')) ? 'badge-success' : 'badge-secondary'}`}>
                        {(subGroup.active !== undefined ? subGroup.active : (subGroup.activeFlag === 'Y' || subGroup.ActiveFlag === 'Y')) ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td>
                      {!isViewerProfile && (
                        <button className="btn btn-sm btn-secondary" onClick={() => { setEditingItem(subGroup); setShowSubGroupModal(true); }}>
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                );
              }}
            />
          </div>
        </div>
      )}

      {/* CRF Fields Tab */}
      {activeTab === 'fields' && (
        <div className="crf-tab-content">
          <div className="crf-tab-header" style={{ justifyContent: 'flex-end' }}>
            
            {!isViewerProfile && (
              <button className="btn btn-primary" onClick={() => { setEditingItem(null); setShowFieldModal(true); }}>
                + Add Report Field
              </button>
            )}
          </div>
          <div className="card">
            <DataGrid
              data={fieldsWithReportSearchMeta}
              searchPlaceholder=""
              searchKeys={[
                'code',
                'description',
                'groupCode',
                'subGroupCode',
                '__searchReportGroup',
                '__searchReportSubGroup',
                'type',
                'validationType',
                'dataType',
                'values',
                'defaultValue',
                'uom',
                'alertOnValue',
                'alertMessage',
                'remark',
                'remarks',
                'cdiscValues',
                'otherValues',
                'formula',
                'lowRange',
                'highRange',
              ]}
              defaultPageSize={10}
              emptyMessage="No fields found."
              columns={[
                { key: 'code', label: 'Field Code' },
                { key: 'description', label: 'Field Description' },
                { key: 'groupCode', label: 'Report Group' },
                { key: 'subGroupCode', label: 'Report Sub Group' },
                { key: 'type', label: 'Field Type' },
                { key: 'dataType', label: 'Data Type' },
                { key: 'values', label: 'Field Value' },
                { key: 'defaultValue', label: 'Default Value' },
                { key: 'uom', label: 'UOM' },
                { key: 'alertOnValue', label: 'Alert On Value' },
                { key: 'alertMessage', label: 'Alert Message' },
                { key: 'remark', label: 'Remarks' },
                { key: 'active', label: 'Active' },
                { key: 'notNull', label: 'Not Null' },
                { key: 'action', label: 'Action' },
              ]}
              renderRow={(field) => {
                const gCode = field.groupCode || field.GroupCode;
                const sgCode = field.subGroupCode || field.SubGroupCode;
                const group = groups.find(g => (g.code || g.Code) === gCode);
                const subGroup = subGroups.find(sg => (sg.code || sg.Code) === sgCode);
                const groupName = group ? (group.description || group.Description) : (gCode || '-');
                const subGroupName = subGroup ? (subGroup.description || subGroup.Description) : (sgCode || '-');
                const fieldType = (field.type || field.Type || '').toString();
                const fieldValueCell =
                  fieldType === 'Formula'
                    ? (field.formula ?? field.Formula ?? '-')
                    : (field.values ?? field.Values ?? '-');
                return (
                <tr key={field.id || field.code}>
                  <td>{field.code || field.Code || '-'}</td>
                  <td>{field.description || field.Description || '-'}</td>
                  <td>{groupName}</td>
                  <td>{subGroupName}</td>
                  <td>{field.type || field.Type || '-'}</td>
                  <td>{field.dataType || field.DataType || '-'}</td>
                  <td title={fieldType === 'Formula' ? (field.formula ?? field.Formula ?? '') : (field.values ?? field.Values ?? '')}>
                    {fieldValueCell || '-'}
                  </td>
                  <td>{field.defaultValue || field.DefaultValue || '-'}</td>
                  <td>{field.uom || field.UOM || '-'}</td>
                  <td>{field.alertOnValue || field.AlertOnValue || '-'}</td>
                  <td>{field.alertMessage || field.AlertMessage || '-'}</td>
                  <td>{field.remark ?? field.Remark ?? '-'}</td>
                  <td>
                    <span className={`badge ${(field.active !== undefined ? field.active : (field.activeFlag === 'Y' || field.ActiveFlag === 'Y')) ? 'badge-success' : 'badge-secondary'}`}>
                      {(field.active !== undefined ? field.active : (field.activeFlag === 'Y' || field.ActiveFlag === 'Y')) ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td>{field.notNull ? 'Yes' : 'No'}</td>
                  <td>
                    {!isViewerProfile && (
                      <button className="btn btn-sm btn-secondary" onClick={() => { setEditingItem(field); setShowFieldModal(true); }}>
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              );
              }}
            />
          </div>
        </div>
      )}

      {/* CRF Template Design Tab */}
      {activeTab === 'design' && (
        <div className="crf-tab-content">
          
          
          <div className="form-group">
            <label className="form-label">Template Name <span className="text-danger">*</span></label>
            <input
              type="text"
              className={`form-input ${designFormErrors.templateName ? 'is-invalid' : ''}`}
              value={templateName}
              onChange={(e) => {
                setTemplateName(e.target.value);
                if (designFormErrors.templateName) setDesignFormErrors((prev) => ({ ...prev, templateName: '' }));
              }}
              placeholder="Enter Template Name"
            />
            {designFormErrors.templateName && <span className="text-danger small">{designFormErrors.templateName}</span>}
          </div>

          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">Report Group <span className="text-danger">*</span></label>
              <select
                className={`form-select ${designFormErrors.selectedGroup ? 'is-invalid' : ''}`}
                value={selectedGroup}
                onChange={(e) => {
                  setSelectedGroup(e.target.value);
                  setSelectedSubGroup('');
                  if (designFormErrors.selectedGroup) setDesignFormErrors((prev) => ({ ...prev, selectedGroup: '' }));
                }}
              >
                <option value="">Select Report Group</option>
                {groups.filter(g => g.active !== false).map(group => (
                  <option key={group.id || group.code} value={group.code || group.Code}>{group.description || group.Description}</option>
                ))}
              </select>
              {designFormErrors.selectedGroup && <span className="text-danger small d-block">{designFormErrors.selectedGroup}</span>}
            </div>
            <div className="form-group">
              <label className="form-label">Report Sub Group <span className="text-danger">*</span></label>
              <select
                className={`form-select ${designFormErrors.selectedSubGroup ? 'is-invalid' : ''}`}
                value={selectedSubGroup}
                style={{ borderColor: '#a19191' }}
                onChange={(e) => {
                  setSelectedSubGroup(e.target.value);
                  if (designFormErrors.selectedSubGroup) setDesignFormErrors((prev) => ({ ...prev, selectedSubGroup: '' }));
                  const filtered = fields.filter(f =>
                    (f.active !== false) && (f.groupCode || f.GroupCode) === selectedGroup && (f.subGroupCode || f.SubGroupCode) === e.target.value
                  );
                  setAvailableFields(filtered);
                }}
                disabled={!selectedGroup}
              >
                <option value="">Select Report Sub Group</option>
                {(() => {
                  const subGroupCodesInGroup = (fields || []).filter(f => (f.groupCode || f.GroupCode) === selectedGroup).map(f => (f.subGroupCode || f.SubGroupCode)).filter(Boolean);
                  let designSubGroups = subGroups.filter(sg => {
                    if (sg.active === false) return false;
                    const sgCode = sg.code || sg.Code;
                    const sgGroupCode = sg.groupCode || sg.GroupCode;
                    return sgGroupCode === selectedGroup || subGroupCodesInGroup.includes(sgCode);
                  });
                  if (selectedGroup && designSubGroups.length === 0) designSubGroups = subGroups.filter(sg => sg.active !== false);
                  return designSubGroups.map(subGroup => (
                    <option key={subGroup.id || subGroup.code} value={subGroup.code || subGroup.Code}>{subGroup.description || subGroup.Description}</option>
                  ));
                })()}
              </select>
              {designFormErrors.selectedSubGroup && <span className="text-danger small d-block">{designFormErrors.selectedSubGroup}</span>}
            </div>
          </div>

          <div className="crf-design-container">
            <div className="crf-available-box">
              <div className="crf-box-header available">
                <span>Available Fields</span>
                <i className="fas fa-list"></i>
              </div>
              <div
                className={`crf-box-content ${dragOverZone === 'available' ? 'crf-drop-over' : ''}`}
                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverZone('available'); }}
                onDragLeave={() => setDragOverZone(null)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOverZone(null);
                  try {
                    const data = JSON.parse(e.dataTransfer.getData('application/json'));
                    if (data.source === 'selected' && data.field) {
                      handleRemoveField(data.field.id || data.field.code);
                    }
                  } catch (_) {}
                }}
              >
                {availableFields.length === 0 ? (
                  <div className="crf-empty-state">Select Report Group and Report Sub Group to see available fields</div>
                ) : (
                  availableFields.map(field => (
                    <div
                      key={field.id || field.code}
                      className="crf-field-item"
                      draggable
                      onDragStart={(e) => {
                        if (e.target.closest('button')) { e.preventDefault(); return; }
                        e.dataTransfer.setData('application/json', JSON.stringify({ field, source: 'available' }));
                        e.dataTransfer.effectAllowed = 'move';
                      }}
                    >
                      <i className="fas fa-file-alt"></i>
                      <span>{field.description || field.Description}</span>
                      <button className="btn btn-sm btn-primary" onClick={() => handleAddField(field)}>
                        Add →
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="crf-selected-box">
              <div className="crf-box-header selected">
                <span>Selected Fields</span>
                <i className="fas fa-check-circle"></i>
              </div>
              <div
                className={`crf-box-content ${dragOverZone === 'selected' ? 'crf-drop-over' : ''}`}
                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverZone('selected'); }}
                onDragLeave={() => setDragOverZone(null)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOverZone(null);
                  try {
                    const data = JSON.parse(e.dataTransfer.getData('application/json'));
                    if (data.source === 'available' && data.field) {
                      handleAddField(data.field);
                    } else if (data.source === 'selected' && data.field) {
                      reorderSelectedFields(data.field.id || data.field.code, null);
                    }
                  } catch (_) {}
                }}
              >
                {selectedFields.length === 0 ? (
                  <div className="crf-empty-state">No fields selected. Add fields from available list.</div>
                ) : (
                  selectedFields.map(field => (
                    <div
                      key={field.id || field.code}
                      className="crf-field-item selected"
                      draggable
                      onDragStart={(e) => {
                        if (e.target.closest('button')) { e.preventDefault(); return; }
                        e.dataTransfer.setData('application/json', JSON.stringify({ field, source: 'selected' }));
                        e.dataTransfer.effectAllowed = 'move';
                      }}
                      onDragEnd={() => setDragOverZone(null)}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        e.dataTransfer.dropEffect = 'move';
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDragOverZone(null);
                        try {
                          const data = JSON.parse(e.dataTransfer.getData('application/json'));
                          if (data.source === 'selected' && data.field) {
                            const dropId = field.id || field.code;
                            const draggedId = data.field.id || data.field.code;
                            if (dropId !== draggedId) {
                              reorderSelectedFields(draggedId, dropId);
                            }
                          }
                        } catch (_) {}
                      }}
                    >
                      <i className="fas fa-file-alt"></i>
                      <span>{field.description || field.Description}</span>
                      <button className="btn btn-sm btn-danger" onClick={() => handleRemoveField(field.id || field.code)}>
                        Remove
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {!isViewerProfile && (
            <div className="crf-save-container">
              <button className="btn btn-primary" onClick={() => {
                if (!validateDesignForm()) return;
                handleSaveTemplate();
              }} disabled={loading}>
                {loading ? 'Saving...' : 'Save'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* CRF Template Management Tab */}
      {activeTab === 'templates' && (
        <div className="crf-tab-content crf-templates-tab">
         
          <DataGrid
            data={templates}
            searchPlaceholder=""
            searchKeys={['templateName', 'TemplateName', 'templateId', 'TemplateId', 'name', 'version', 'Version']}
            defaultPageSize={12}
            emptyMessage="No templates found."
            gridLayout="custom"
          >
            {(paginatedData) => (
              <div className="crf-templates-grid">
                {paginatedData.map(template => {
                  const templateId = template.templateId || template.TemplateId || template.id || template.templateHeaderNo;
                  const templateName = template.templateName || template.TemplateName || template.name || templateId || 'Unnamed Template';
                  return (
                    <div key={templateId} className="card crf-template-card">
                      <h4>{templateName}</h4>
                      <div className="crf-template-badges">
                        <span className="badge badge-success">v{template.version || template.Version || '1.0'}</span>
                        <span className={`badge ${(template.isActive !== false && template.IsActive !== false) ? 'badge-info' : 'badge-secondary'}`}>
                          {(template.isActive !== false && template.IsActive !== false) ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div className={`crf-template-actions${isViewerProfile ? ' crf-template-actions--viewer' : ''}`}>
                        <div className="crf-template-actions-row crf-template-actions-row-1">
                          <button className="btn btn-sm btn-primary" onClick={() => handlePreviewTemplate(template)}>Preview</button>
                          {!isViewerProfile && (
                            <>
                              <button className="btn btn-sm btn-primary" onClick={async () => {
                                const canEdit = await checkTemplateCanEdit(template);
                                if (canEdit) { setEditingItem(template); setShowTemplateEditModal(true); }
                              }}>Edit</button>
                              <button className="btn btn-sm btn-primary" onClick={() => handleEditTemplateFields(template)}>Edit Fields</button>
                              <button className="btn btn-sm btn-primary" onClick={() => {
                                setConfirmDialog({
                                  open: true,
                                  title: 'Copy Template',
                                  message: `Create a copy of "${template.templateName || template.TemplateName || templateId}"? The new template will have the next version and can be mapped to visits separately.`,
                                  confirmText: 'Yes',
                                  cancelText: 'No',
                                  variant: 'primary',
                                  onConfirm: () => {
                                    handleCopyTemplate(template);
                                  },
                                });
                              }}>Copy</button>
                            </>
                          )}
                        </div>
                        <div className="crf-template-actions-row crf-template-actions-row-2">
                          {!isViewerProfile && (
                            <button className="btn btn-sm btn-primary crf-btn-map" onClick={() => handleMapTemplate(template)}>Map to Visit</button>
                          )}
                          <button className="btn btn-sm btn-primary" onClick={() => handleShowMapping(template)} title="View assigned Project, Visit Type">Show Mapping</button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </DataGrid>
        </div>
      )}

      {/* Report Group Modal */}
      <Modal
        isOpen={showGroupModal}
        onClose={() => {
          setShowGroupModal(false);
          setEditingItem(null);
          setGroupFormErrors({});
        }}
        title={editingItem ? 'Edit Report Group' : 'Add Report Group'}
        size="medium"
        footer={
          <>
           
            <button className="btn btn-primary" onClick={(e) => {
              e.preventDefault();
              const form = document.querySelector('.crf-group-form');
              if (!form) return;
              const desc = (form.querySelector('input[name="description"]')?.value || '').trim();
              const remarks = (form.querySelector('textarea[name="remarks"]')?.value || '').trim();
              const errs = {};
              if (!desc) errs.description = crfMessages.attributeGroupName;
              if (!remarks) errs.remarks = crfMessages.remarksRequired;
              const currentCode = editingItem ? (editingItem.code ?? editingItem.Code ?? editingItem.id) : undefined;
              if (desc && isDuplicateDescription(groups, desc, currentCode)) errs.description = crfMessages.attributeGroupDuplicate;
              setGroupFormErrors(errs);
              if (Object.keys(errs).length > 0) return;
              const formDataObj = new FormData(form);
              const activeCheckbox = form.querySelector('input[name="active"]');
              const data = {
                code: editingItem ? (formDataObj.get('code') || editingItem.code || editingItem.Code) : undefined,
                description: formDataObj.get('description'),
                remarks: formDataObj.get('remarks') || '',
                active: editingItem ? (activeCheckbox ? activeCheckbox.checked : false) : true,
              };
              handleSaveGroup(data);
            }}>
              {editingItem ? 'Update' : 'Save'}
            </button>
            <button className="btn btn-secondary" onClick={() => { setShowGroupModal(false); setEditingItem(null); setGroupFormErrors({}); }}>
              Cancel
            </button>
          </>
        }
      >
        <CRFGroupForm
          group={editingItem}
          groups={groups}
          fieldErrors={groupFormErrors}
        />
      </Modal>

      {/* Report Sub Group Modal */}
      <Modal
        isOpen={showSubGroupModal}
        onClose={() => {
          setShowSubGroupModal(false);
          setEditingItem(null);
          setSubGroupFormErrors({});
        }}
        title={editingItem ? 'Edit Report Sub Group' : 'Add Report Sub Group'}
        size="medium"
        footer={
          <>
            
            <button className="btn btn-primary" onClick={(e) => {
              e.preventDefault();
              const form = document.querySelector('.crf-subgroup-form');
              if (!form) return;
              const desc = (form.querySelector('input[name="description"]')?.value || '').trim();
              const remarks = (form.querySelector('textarea[name="remarks"]')?.value || '').trim();
              const errs = {};
              if (!desc) errs.description = crfMessages.attributeSubGroupName;
              if (!remarks) errs.remarks = crfMessages.remarksRequired;
              const currentSubCode = editingItem ? (editingItem.code ?? editingItem.Code ?? editingItem.id) : undefined;
              if (desc && isDuplicateDescription(subGroups, desc, currentSubCode)) errs.description = crfMessages.attributeSubGroupDuplicate;
              setSubGroupFormErrors(errs);
              if (Object.keys(errs).length > 0) return;
              const formDataObj = new FormData(form);
              const activeCheckbox = form.querySelector('input[name="active"]');
              const data = {
                code: editingItem ? (formDataObj.get('code') || editingItem.code || editingItem.Code || editingItem.id) : undefined,
                description: formDataObj.get('description'),
                groupCode: editingItem ? (editingItem.groupCode || editingItem.GroupCode) : undefined,
                remarks: remarks,
                active: editingItem ? (activeCheckbox ? activeCheckbox.checked : false) : true,
              };
              handleSaveSubGroup(data);
            }}>
              {editingItem ? 'Update' : 'Save'}
            </button>
            <button className="btn btn-secondary" onClick={() => { setShowSubGroupModal(false); setEditingItem(null); setSubGroupFormErrors({}); }}>
              Cancel
            </button>
          </>
        }
      >
        <CRFSubGroupForm
          subGroup={editingItem}
          groups={groups}
          fieldErrors={subGroupFormErrors}
        />
      </Modal>

      {/* CRF Field Modal */}
      <Modal
        isOpen={showFieldModal}
        onClose={() => {
          setShowFieldModal(false);
          setEditingItem(null);
          setFieldFormErrors({});
        }}
        title={editingItem ? 'Edit Report Field' : 'Add Report Field'}
        size="large"
        footer={
          <>
            
            <button className="btn btn-primary" onClick={(e) => {
              e.preventDefault();
              const form = document.querySelector('.crf-field-form');
              if (!form) return;
              const desc = (form.querySelector('input[name="description"]')?.value || '').trim();
              const groupCode = form.querySelector('select[name="groupCode"]')?.value || '';
              const subGroupCode = form.querySelector('select[name="subGroupCode"]')?.value || '';
              const type = form.querySelector('select[name="type"]')?.value || '';
              const values = (form.querySelector('input[name="values"]')?.value || '').trim();
              const errs = {};
              if (!desc) errs.description = crfMessages.attributeDescription;
              if (!groupCode) errs.groupCode = crfMessages.attributeGroupSelect;
              if (!subGroupCode) errs.subGroupCode = crfMessages.attributeSubGroupSelect;
              if (!type) errs.type = crfMessages.attributeTypeSelect;
              const typesRequiringValue = ['Label', 'ComboBox', 'Radio', 'Checkbox'];
              if (typesRequiringValue.includes(type) && !values) errs.values = crfMessages.attributeLabelValueRequired;
              if (type === 'Associated') {
                const refCol = (form.querySelector('input[name="refColumn"]')?.value || '').trim();
                if (!refCol) errs.associated = 'Configure the association before saving.';
                else {
                  try {
                    const j = JSON.parse(refCol);
                    if (!j.driverFieldCode) errs.associated = 'Drag a control field into the association builder.';
                  } catch {
                    errs.associated = 'Invalid association configuration.';
                  }
                }
              }
              const remarkVal = (form.querySelector('input[name="remark"]')?.value || '').trim();
              if (!remarkVal) errs.remark = crfMessages.remarksRequired;
              const currentFieldCode = editingItem ? (editingItem.code ?? editingItem.Code ?? editingItem.id) : undefined;
              if (desc && isDuplicateDescription(fields, desc, currentFieldCode)) errs.description = crfMessages.attributeDescriptionDuplicate;
              setFieldFormErrors(errs);
              if (Object.keys(errs).length > 0) return;
              const formDataObj = new FormData(form);
              const activeCheckbox = form.querySelector('input[name="active"]');
              const notNullCheckbox = form.querySelector('input[name="notNull"]');
              const data = {
                code: formDataObj.get('code'),
                description: formDataObj.get('description'),
                groupCode: formDataObj.get('groupCode'),
                subGroupCode: formDataObj.get('subGroupCode'),
                type: formDataObj.get('type'),
                validationType: formDataObj.get('validationType') || 'NA',
                defaultValue: formDataObj.get('defaultValue') || '',
                uom: formDataObj.get('uom') || '',
                alertOnValue: formDataObj.get('alertOnValue') || '',
                alertMessage: formDataObj.get('alertMessage') || '',
                values: formDataObj.get('values') || '',
                active: editingItem ? (activeCheckbox ? activeCheckbox.checked : false) : true,
                notNull: notNullCheckbox ? notNullCheckbox.checked : false,
                remark: formDataObj.get('remark') || '',
                refType: formDataObj.get('refType') || '',
                refTable: formDataObj.get('refTable') || '',
                refColumn: formDataObj.get('refColumn') || '',
                refFilePath: formDataObj.get('refFilePath') || '',
                alertType: formDataObj.get('alertType') || '',
                cdiscValues: formDataObj.get('cdiscValues') || '',
                otherValues: formDataObj.get('otherValues') || '',
                currentDate: formDataObj.get('currentDate') || '',
                sequence: (() => {
                  const raw = formDataObj.get('sequence');
                  if (raw === '' || raw == null) return null;
                  const n = parseInt(raw, 10);
                  return Number.isNaN(n) ? null : n;
                })(),
                formula: formDataObj.get('formula') || '',
                visitReportFillHint: (form.querySelector('textarea[name="visitReportFillHint"]')?.value || '').trim(),
              };
              handleSaveField(data);
            }}>
              {editingItem ? 'Update' : 'Save'}
            </button>
            <button className="btn btn-secondary" onClick={() => { setShowFieldModal(false); setEditingItem(null); setFieldFormErrors({}); }}>
              Cancel
            </button>
          </>
        }
      >
        <CRFFieldForm
          field={editingItem}
          groups={groups}
          subGroups={subGroups}
          fields={fields}
          fieldErrors={fieldFormErrors}
        />
      </Modal>

      {/* CRF Template Edit Modal */}
      <Modal
        isOpen={showTemplateEditModal}
        onClose={() => {
          setShowTemplateEditModal(false);
          setEditingItem(null);
        }}
        title="Edit Report Template"
        size="medium"
        footer={
          <>
            <button className="btn btn-primary" onClick={async (e) => {
              e.preventDefault();
              e.stopPropagation();
              
              // Find form - try multiple selectors for robustness
              const form = document.querySelector('.crf-template-edit-form') || 
                          document.querySelector('.modal-body form') ||
                          e.target.closest('.modal-content')?.querySelector('form');
              
              if (!form) {
                console.error('Form not found');
                setToastMessage({ type: 'error', message: 'Form not found. Please try again.' });
                return;
              }
              
              // Validate form
              if (!form.checkValidity()) {
                form.reportValidity();
                return;
              }
              
              // Get form data
              const formDataObj = new FormData(form);
              
              // Get templateId from editingItem (disabled inputs don't appear in FormData)
              // Handle both camelCase and PascalCase
              const templateId = editingItem?.templateId || 
                                editingItem?.TemplateId || 
                                editingItem?.id ||
                                editingItem?.templateHeaderNo ||
                                formDataObj.get('templateId');
              
              // Get templateName from form
              const templateName = formDataObj.get('templateName')?.trim() || '';
              
              // Validate required fields
              if (!templateName) {
                setToastMessage({ type: 'error', message: crfMessages.templateNameRequired });
                const nameInput = form.querySelector('input[name="templateName"]');
                if (nameInput) {
                  nameInput.focus();
                  nameInput.reportValidity();
                }
                return;
              }
              
              if (!templateId) {
                setToastMessage({ type: 'error', message: crfMessages.onlyDraftEditable });
                console.error('Template ID not found in editingItem:', editingItem);
                return;
              }
              
              const statusVal = formDataObj.get('status');
              const isActive = statusVal === 'Active';
              // If setting template to Inactive, ensure no active mappings exist
              if (!isActive) {
                try {
                  const res = await api.get(`/crftemplate/templates/${templateId}/mappings`);
                  const mappings = Array.isArray(res.data) ? res.data : [];
                  if (mappings.length > 0) {
                    setToastMessage({ type: 'error', message: 'This template is mapped to one or more projects. Remove all mappings before making the template inactive.' });
                    return;
                  }
                } catch (err) {
                  console.error('Error checking template mappings:', err);
                  setToastMessage({ type: 'error', message: err.response?.data?.error || 'Could not verify mappings. Please try again.' });
                  return;
                }
              }
              const data = {
                templateName: templateName,
                templateId: templateId,
                isActive: isActive,
                version: formDataObj.get('version') || '1.0',
                description: formDataObj.get('description') || '',
              };
              handleSaveTemplateEdit(data);
            }}>
              Update
            </button>
            <button className="btn btn-secondary" onClick={() => { 
              setShowTemplateEditModal(false); 
              setEditingItem(null); 
            }}>
              Cancel
            </button>
          </>
        }
      >
        <CRFTemplateEditForm
          template={editingItem}
        />
      </Modal>

      {/* CRF Template Mapping Modal */}
      <Modal
        isOpen={showTemplateMappingModal}
        onClose={() => {
          setShowTemplateMappingModal(false);
          setMappingFormErrors({});
          setMappingForm({
            templateId: null,
            projectCode: '',
            visitType: ''
          });
        }}
        title="Map Template to Visit"
        size="medium"
        footer={
          <>
          <button className="btn btn-primary" onClick={() => {
              if (!validateMappingForm()) return;
              handleSaveMapping();
            }} disabled={loading}>
              {loading ? 'Saving...' : 'Save'}
            </button>
            <button className="btn btn-secondary" onClick={() => {
              setShowTemplateMappingModal(false);
              setMappingForm({
                templateId: null,
                projectCode: '',
                visitType: ''
              });
            }}>
              Cancel
            </button>
            
          </>
        }
      >
        <CRFTemplateMappingForm
          mappingForm={mappingForm}
          mappingFormErrors={mappingFormErrors}
          projects={projects}
          onMappingChange={handleMappingChange}
          loading={loading}
          projectDisabled={Boolean(currentProject && currentProject !== 'all')}
        />
      </Modal>

      {/* Show Mapping Modal - assigned Project + Visit Type for template */}
      <Modal
        isOpen={showMappingListModal}
        onClose={() => { setShowMappingListModal(false); setMappingListTemplate(null); setTemplateMappings([]); }}
        title={mappingListTemplate ? `Template Mapping - ${mappingListTemplate.templateName || mappingListTemplate.TemplateName || mappingListTemplate.templateId || mappingListTemplate.TemplateId || 'Template'}` : 'Template Mapping'}
        size="large"
      >
        {loadingMappings ? (
          <div className="text-center p-4"><Spinner /></div>
        ) : (
          <div className="crf-mapping-list">
            {templateMappings.length === 0 ? (
              <p className="text-muted mb-0">No Project/Visit Type assignments for this template. Use &quot;Map to Visit&quot; to assign.</p>
            ) : (
              <div className="table-responsive">
                <table className="crf-table">
                  <thead>
                    <tr>
                      <th>Project</th>
                      <th>Visit Type</th>
                      {!isViewerProfile && <th style={{ width: '100px' }}>Action</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {templateMappings.map((row, idx) => {
                      const canRemove = row.allowRemove !== false && row.AllowRemove !== false;
                      return (
                      <tr key={row.id ?? idx}>
                        <td>{row.projectCode ?? row.ProjectCode ?? '-'}</td>
                        <td>{row.visitTypeDisplayName ?? row.VisitTypeDisplayName ?? (row.visitType ?? row.VisitType) ?? '-'}</td>
                        {!isViewerProfile && (
                          <td>
                            <button
                              type="button"
                              className="btn btn-sm btn-danger"
                              disabled={!canRemove}
                              onClick={() => {
                                if (!canRemove) {
                                  setToastMessage({ type: 'error', message: crfMessages.cannotRemoveMapping });
                                  return;
                                }
                                const mappingId = row.id ?? row.Id;
                                setConfirmDialog({
                                  open: true,
                                  title: 'Remove Assignment',
                                  message: 'Remove this assignment? (Project and visit type will no longer use this template for newly scheduled visits.)',
                                  confirmText: 'Yes',
                                  cancelText: 'No',
                                  variant: 'danger',
                                  onConfirm: () => {
                                    handleRemoveMapping(mappingId);
                                  },
                                });
                              }}
                              title={canRemove ? 'Remove this assignment' : crfMessages.cannotRemoveMapping}
                            >
                              Remove
                            </button>
                          </td>
                        )}
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* CRF Template Fields Edit Modal */}
      <Modal
        isOpen={showTemplateFieldsModal}
        onClose={() => {
          setShowTemplateFieldsModal(false);
          setEditingTemplate(null);
          setTemplateFields([]);
          setSelectedFieldsForTemplate([]);
          setAvailableFieldsForTemplate([]);
          setSelectedGroupForTemplate('');
          setSelectedSubGroupForTemplate('');
          setTemplateFieldsFilterErrors({});
        }}
        title={editingTemplate ? `Edit Fields - ${editingTemplate.templateName || editingTemplate.TemplateName || 'Template'}` : 'Edit Template Fields'}
        size="large"
        footer={
          <>
            <button className="btn btn-primary" onClick={() => {
              if (!validateTemplateFieldsFilters()) return;
              handleSaveTemplateFields();
              }} disabled={loading}>
              {loading ? 'Saving...' : 'Update'}
            </button>
            <button className="btn btn-secondary" onClick={() => {
              setShowTemplateFieldsModal(false);
              setEditingTemplate(null);
              setTemplateFields([]);
              setSelectedFieldsForTemplate([]);
              setAvailableFieldsForTemplate([]);
              setSelectedGroupForTemplate('');
              setSelectedSubGroupForTemplate('');
              setTemplateFieldsFilterErrors({});
            }}>
              Cancel
            </button>
          </>
        }
      >
        <CRFTemplateFieldsEditForm
          availableFields={availableFieldsForTemplate}
          selectedFields={selectedFieldsForTemplate}
          onAddField={handleAddFieldToTemplate}
          onRemoveField={handleRemoveFieldFromTemplate}
          onReorderFields={reorderSelectedFieldsForTemplate}
          groups={groups}
          subGroups={subGroups}
          selectedGroup={selectedGroupForTemplate}
          selectedSubGroup={selectedSubGroupForTemplate}
          onGroupChange={handleGroupChangeForTemplate}
          onSubGroupChange={handleSubGroupChangeForTemplate}
          filterErrors={templateFieldsFilterErrors}
          loading={loading}
        />
      </Modal>

      {/* Custom confirm dialog (replaces browser confirm) */}
      <ConfirmDialog
        isOpen={confirmDialog.open}
        onClose={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
        variant={confirmDialog.variant}
      />

      {/* Toast for success/error messages (replaces alert) */}
      {toastMessage && (
        <div className="crf-toast-wrapper" style={{ position: 'fixed', top: '1rem', right: '1rem', zIndex: 9999 }}>
          <Alert
            type={toastMessage.type}
            message={toastMessage.message}
            onClose={() => setToastMessage(null)}
            autoClose={true}
          />
        </div>
      )}
    </div>
  );
};

const fieldErrorStyle = { color: '#c00', fontSize: '12px', marginTop: '4px' };

// Report Group Form Component
const CRFGroupForm = ({ group, fieldErrors = {} }) => {
  // Handle both camelCase and PascalCase, and convert ActiveFlag to boolean
  const getActiveValue = (item) => {
    if (item?.active !== undefined) return item.active;
    if (item?.activeFlag === 'Y' || item?.ActiveFlag === 'Y') return true;
    if (item?.activeFlag === 'N' || item?.ActiveFlag === 'N') return false;
    return true; // default
  };
  
  const [formData, setFormData] = useState({
    code: group?.code || group?.Code || '',
    description: group?.description || group?.Description || '',
    remarks: group != null ? '' : (group?.remarks || group?.Remarks || ''),
    active: getActiveValue(group),
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  return (
    <form className="crf-group-form">
      <div className="form-group">
        <label className="form-label">Report Group <span className="text-danger">*</span></label>
        <input
          type="text"
          name="description"
          className="form-input"
          value={formData.description}
          onChange={handleChange}
          placeholder="Enter Report Group"
        />
        {fieldErrors.description && <div className="field-error" style={fieldErrorStyle}>{fieldErrors.description}</div>}
      </div>

      <div className="form-group">
        <label className="form-label">Remarks <span className="text-danger">*</span></label>
        <textarea
          name="remarks"
          className="form-input"
          value={formData.remarks}
          onChange={handleChange}
          rows="3"
          placeholder="Enter Remarks"
        />
        {fieldErrors.remarks && <div className="field-error" style={fieldErrorStyle}>{fieldErrors.remarks}</div>}
      </div>

      {group != null && (
        <div className="form-group">
          <label className="form-label">
            <input
              type="checkbox"
              name="active"
              checked={formData.active}
              onChange={handleChange}
            />
            <span style={{ marginLeft: '8px' }}>Active</span>
          </label>
        </div>
      )}
    </form>
  );
};

// Report Sub Group Form Component
const CRFSubGroupForm = ({ subGroup, groups, fieldErrors = {} }) => {
  // Handle both camelCase and PascalCase, and convert ActiveFlag to boolean
  const getActiveValue = (item) => {
    if (item?.active !== undefined) return item.active;
    if (item?.activeFlag === 'Y' || item?.ActiveFlag === 'Y') return true;
    if (item?.activeFlag === 'N' || item?.ActiveFlag === 'N') return false;
    return true; // default
  };
  
  const [formData, setFormData] = useState({
    code: subGroup?.code || subGroup?.Code || '',
    description: subGroup?.description || subGroup?.Description || '',
    groupCode: subGroup?.groupCode || subGroup?.GroupCode || '',
    remarks: subGroup != null ? '' : (subGroup?.remark ?? subGroup?.remarks ?? subGroup?.Remark ?? subGroup?.Remarks ?? ''),
    active: getActiveValue(subGroup),
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  return (
    <form className="crf-subgroup-form">
      <div className="form-group">
        <label className="form-label">Report Sub Group <span className="text-danger">*</span></label>
        <input
          type="text"
          name="description"
          className="form-input"
          value={formData.description}
          onChange={handleChange}
          placeholder="Enter Report Sub Group"
        />
        {fieldErrors.description && <div className="field-error" style={fieldErrorStyle}>{fieldErrors.description}</div>}
      </div>

      <div className="form-group">
        <label className="form-label">Remarks <span className="text-danger">*</span></label>
        <textarea
          name="remarks"
          className="form-input form-textarea"
          value={formData.remarks}
          onChange={handleChange}
          placeholder="Enter Remarks"
          rows={3}
        />
        {fieldErrors.remarks && <div className="field-error" style={fieldErrorStyle}>{fieldErrors.remarks}</div>}
      </div>

      {subGroup != null && (
        <div className="form-group">
          <label className="form-label">
            <input
              type="checkbox"
              name="active"
              checked={formData.active}
              onChange={handleChange}
            />
            <span style={{ marginLeft: '8px' }}>Active</span>
          </label>
        </div>
      )}
    </form>
  );
};

/** Branch keys for Associated JSON (must match CRFForm runtime). */
const getBranchKeysForDriver = (driverType, valuesStr) => {
  const t = (driverType || '').trim();
  const parts = (valuesStr || '').split(',').map((s) => s.trim()).filter(Boolean);
  if (t === 'ComboBox' || t === 'Radio') return parts.length ? parts : ['__default__'];
  if (t === 'CheckBox') return parts.length ? parts : ['__checked__', '__unchecked__'];
  return ['__default__'];
};

const normalizeBranchMap = (keys, prev) => {
  const out = {};
  keys.forEach((k) => {
    out[k] = (prev && Array.isArray(prev[k]) ? prev[k] : []).filter(Boolean);
  });
  return out;
};

const isDriverAllowedFieldType = (type) => {
  if (type == null || type === '') return false;
  const n = String(type).trim().toLowerCase().replace(/\s+/g, '');
  return n === 'radio';
};

/** Radio associations: only the Yes branch accepts drops when the control has a literal Yes option. */
const isAssociatedRadioBranchDroppable = (driverType, branchKey, allBranchKeys) => {
  if (!isDriverAllowedFieldType(driverType)) return true;
  const keys = Array.isArray(allBranchKeys) ? allBranchKeys : [];
  const hasYesOption = keys.some((k) => String(k || '').trim().toLowerCase() === 'yes');
  if (!hasYesOption) return true;
  return String(branchKey || '').trim().toLowerCase() === 'yes';
};

// CRF Field Form Component
const CRFFieldForm = ({ field, groups, subGroups, fields = [], fieldErrors = {} }) => {
  const { currentProject } = useProject();
  const [showAssociatedModal, setShowAssociatedModal] = useState(false);
  // Handle both camelCase and PascalCase, and convert ActiveFlag to boolean
  const getActiveValue = (item) => {
    if (item?.active !== undefined) return item.active;
    if (item?.activeFlag === 'Y' || item?.ActiveFlag === 'Y') return true;
    if (item?.activeFlag === 'N' || item?.ActiveFlag === 'N') return false;
    return true; // default
  };
  
  const [formData, setFormData] = useState({
    code: field?.code || field?.Code || '',
    description: field?.description || field?.Description || '',
    groupCode: field?.groupCode || field?.GroupCode || '',
    subGroupCode: field?.subGroupCode || field?.SubGroupCode || '',
    type: field?.type ?? field?.Type ?? '',
    validationType: field?.validationType || field?.ValidationType || 'NA',
    defaultValue: field?.defaultValue || field?.DefaultValue || '',
    uom: field?.uom || field?.UOM || '',
    alertOnValue: field?.alertOnValue || field?.AlertOnValue || '',
    alertMessage: field?.alertMessage || field?.AlertMessage || '',
    values: field?.values || field?.Values || '',
    active: getActiveValue(field),
    notNull: field?.notNull ?? field?.NotNull ?? (field?.isNABL === 1 || field?.IsNABL === 1) ?? false,
    sequence: field?.sequence ?? field?.Sequence ?? null,
    remark: field != null ? '' : (field?.remark || field?.Remark || ''),
    visitReportFillHint: field?.visitReportFillHint ?? field?.VisitReportFillHint ?? '',
    refType: field?.refType ?? field?.RefType ?? '',
    refTable: field?.refTable || field?.RefTable || '',
    refColumn: field?.refColumn || field?.RefColumn || '',
    refFilePath: field?.refFilePath || field?.RefFilePath || '',
    alertType: field?.alertType ?? field?.AlertType ?? '',
    cdiscValues: field?.cdiscValues || field?.CDISCValues || '',
    otherValues: field?.otherValues || field?.OtherValues || '',
    currentDate: field?.currentDate ?? field?.CurrentDate ?? '',
    formula: field?.formula ?? field?.Formula ?? '',
  });
  const [showFormulaModal, setShowFormulaModal] = useState(false);
  const projectCode = typeof currentProject === 'string' ? currentProject : (currentProject?.projectCode || currentProject?.ProjectCode || '');

  // Sync form only when editing a different field (by code), not on every field reference change
  useEffect(() => {
    setFormData({
      code: field?.code || field?.Code || '',
      description: field?.description || field?.Description || '',
      groupCode: field?.groupCode || field?.GroupCode || '',
      subGroupCode: field?.subGroupCode || field?.SubGroupCode || '',
      type: field?.type ?? field?.Type ?? '',
      validationType: field?.validationType || field?.ValidationType || 'NA',
      defaultValue: field?.defaultValue || field?.DefaultValue || '',
      uom: field?.uom || field?.UOM || '',
      alertOnValue: field?.alertOnValue || field?.AlertOnValue || '',
      alertMessage: field?.alertMessage || field?.AlertMessage || '',
      values: field?.values || field?.Values || '',
      active: getActiveValue(field),
      notNull: field?.notNull ?? field?.NotNull ?? (field?.isNABL === 1 || field?.IsNABL === 1) ?? false,
      sequence: field?.sequence ?? field?.Sequence ?? null,
      remark: field != null ? '' : (field?.remark || field?.Remark || ''),
      visitReportFillHint: field?.visitReportFillHint ?? field?.VisitReportFillHint ?? '',
      refType: field?.refType ?? field?.RefType ?? '',
      refTable: field?.refTable || field?.RefTable || '',
      refColumn: field?.refColumn || field?.RefColumn || '',
      refFilePath: field?.refFilePath || field?.RefFilePath || '',
      alertType: field?.alertType ?? field?.AlertType ?? '',
      cdiscValues: field?.cdiscValues || field?.CDISCValues || '',
      otherValues: field?.otherValues || field?.OtherValues || '',
      currentDate: field?.currentDate ?? field?.CurrentDate ?? '',
      formula: field?.formula ?? field?.Formula ?? '',
    });
  }, [field?.code ?? field?.Code]);

  // Attribute types aligned with Ramp + sCRFHtmlGenerator (order: common first, then extended)
  const fieldTypes = [
    'TextBox', 'ComboBox', 'CheckBox', 'TextArea', 'DateTime', 'Time', 'File', 'Label', 'Radio', 'Formula', 'Associated', 'StandardDate', 'StandardDateTime', 'STSAutoCapture'
  ];
  // Validation codes for vValidationType (Ramp: AN, AL, IN, NA, NU)
  const validationTypes = [
    { value: 'NA', label: 'Not Applicable' },
    { value: 'AN', label: 'Alpha Numeric' },
    { value: 'AL', label: 'Alphabet' },
    { value: 'IN', label: 'Integer' },
    { value: 'NU', label: 'Numeric' },
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => {
      const next = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      };

      if (name === 'type') {
        if (value !== 'Formula') next.formula = '';
        if (value !== 'Associated') next.refColumn = '';
        if (value === 'STSAutoCapture') {
          next.values = '';
          next.defaultValue = '';
        }
      }

      return next;
    });
  };

  // Groups and sub groups are independent: any group can be selected with any subgroup.
  const allActiveSubGroups = subGroups.filter(sg => sg.active !== false);

  return (
    <>
    <form className="crf-field-form">
      <div className="form-group">
        <label className="form-label">Field Description <span className="text-danger">*</span></label>
        <input
          type="text"
          name="description"
          className="form-input"
          value={formData.description}
          onChange={handleChange}
          placeholder="Enter Field Description"
        />
        {fieldErrors.description && <div className="field-error" style={fieldErrorStyle}>{fieldErrors.description}</div>}
        
      </div>

      <div className="grid-2">
        <div className="form-group">
          <label className="form-label">Report Group <span className="text-danger">*</span></label>
          <select
            name="groupCode"
            className="form-select"
            value={formData.groupCode}
            onChange={handleChange}
            disabled={!!field}
          >
            <option value="">Select Report Group</option>
            {groups.filter(g => g.active !== false).map(g => (
              <option key={g.id || g.code} value={g.code || g.Code}>{g.description || g.Description}</option>
            ))}
          </select>
          {fieldErrors.groupCode && <div className="field-error" style={fieldErrorStyle}>{fieldErrors.groupCode}</div>}
        </div>

        <div className="form-group">
          <label className="form-label">Report Sub Group <span className="text-danger">*</span></label>
          <select
            name="subGroupCode"
            className="form-select"
            value={formData.subGroupCode}
            onChange={handleChange}
            disabled={!!field}
          >
            <option value="">Select Report Sub Group</option>
            {allActiveSubGroups.map(sg => (
              <option key={sg.id || sg.code} value={sg.code || sg.Code}>{sg.description || sg.Description}</option>
            ))}
          </select>
          {fieldErrors.subGroupCode && <div className="field-error" style={fieldErrorStyle}>{fieldErrors.subGroupCode}</div>}
        </div>
      </div>

      <div className="grid-2">
        <div className="form-group">
          <label className="form-label">Field Type <span className="text-danger">*</span></label>
          <select
            name="type"
            className="form-select"
            value={formData.type}
            onChange={handleChange}
          >
            <option value="">Select Field Type</option>
            {fieldTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
          {fieldErrors.type && <div className="field-error" style={fieldErrorStyle}>{fieldErrors.type}</div>}
        </div>

        <div className="form-group">
          <label className="form-label">Data Type</label>
          <select
            name="validationType"
            className="form-select"
            value={formData.validationType}
            onChange={handleChange}
          >
            {validationTypes.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          
        </div>
      </div>

      {formData.type === 'Formula' && (
        <div className="form-group">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setShowFormulaModal(true)}
            disabled={!formData.groupCode || !formData.subGroupCode}
          >
            {formData.formula ? 'Edit Formula' : 'Create Formula'}
          </button>
          {(!formData.groupCode || !formData.subGroupCode) && (
            <small className="text-muted d-block mt-1">Select Report Group and Report Sub Group to create or edit a formula.</small>
          )}
          {formData.formula && (
            <small className="text-muted d-block mt-1">Formula: {formData.formula}</small>
          )}
        </div>
      )}

      {formData.type === 'Associated' && (
        <div className="form-group">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setShowAssociatedModal(true)}
            disabled={!formData.groupCode || !formData.subGroupCode}
          >
            {formData.refColumn ? 'Edit Association' : 'Create Association'}
          </button>
          {(!formData.groupCode || !formData.subGroupCode) && (
            <small className="text-muted d-block mt-1">Select Report Group and Report Sub Group first.</small>
          )}
          {fieldErrors.associated && <div className="field-error" style={fieldErrorStyle}>{fieldErrors.associated}</div>}
        </div>
      )}

      <input type="hidden" name="formula" value={formData.formula || ''} readOnly />
      <input type="hidden" name="refColumn" value={formData.refColumn || ''} readOnly />

      {formData.type !== 'STSAutoCapture' && formData.type !== 'Associated' && (
        <div className="form-group">
          <label className="form-label">
            Field Value{['Label', 'ComboBox', 'Radio', 'CheckBox'].includes(formData.type) ? <span className="text-danger"> *</span> : ''}
          </label>
          <input
            type="text"
            name="values"
            className="form-input"
            value={formData.values}
            onChange={handleChange}
            placeholder="e.g., Yes,No or Option1,Option2,Option3"
          />
          {fieldErrors.values && <div className="field-error" style={fieldErrorStyle}>{fieldErrors.values}</div>}
          
        </div>
      )}

      <div className="grid-2">
        {formData.type !== 'STSAutoCapture' && (
          <div className="form-group">
            <label className="form-label">Default Value</label>
            <input
              type="text"
              name="defaultValue"
              className="form-input"
              value={formData.defaultValue}
              onChange={handleChange}
              placeholder="Enter default value"
            />
          </div>
        )}

        <div className="form-group">
          <label className="form-label">Unit of Measure (UOM)</label>
          <input
            type="text"
            name="uom"
            className="form-input"
            value={formData.uom}
            onChange={handleChange}
            placeholder="e.g., kg, cm, mg"
          />
        </div>
      </div>

      <div className="grid-2">
        <div className="form-group">
          <label className="form-label">Alert On Value</label>
          <input
            type="text"
            name="alertOnValue"
            className="form-input"
            value={formData.alertOnValue}
            onChange={handleChange}
            placeholder="Value that triggers alert"
          />
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Alert Message</label>
        <textarea
          name="alertMessage"
          className="form-input"
          value={formData.alertMessage}
          onChange={handleChange}
          rows="2"
          placeholder="Enter alert message"
        />
      </div>

      <div className="form-group">
        <label className="form-label">Remarks <span className="text-danger">*</span></label>
        <input
          type="text"
          name="remark"
          className="form-input"
          value={formData.remark}
          onChange={handleChange}
          placeholder="Enter Remarks"
        />
        {fieldErrors.remark && <div className="field-error" style={fieldErrorStyle}>{fieldErrors.remark}</div>}
      </div>

      <div className="form-group">
        <label className="form-label">Report Field Suggestion Value</label>
        <textarea
          name="visitReportFillHint"
          className="form-input"
          value={formData.visitReportFillHint}
          onChange={handleChange}
          rows={3}
          maxLength={2000}
          placeholder="Text shown on (i) hover in the Visit Report for this question."
          required
        />
        {fieldErrors.visitReportFillHint && <div className="field-error" style={fieldErrorStyle}>{fieldErrors.visitReportFillHint}</div>}
      </div>

      <div className="grid-2">
        {field != null && (
          <div className="form-group">
            <label className="form-label">
              <input
                type="checkbox"
                name="active"
                checked={formData.active}
                onChange={handleChange}
              />
              <span style={{ marginLeft: '8px' }}>Active</span>
            </label>
          </div>
        )}

        <div className="form-group">
          <label className="form-label">
            <input
              type="checkbox"
              name="notNull"
              checked={formData.notNull}
              onChange={handleChange}
            />
            <span style={{ marginLeft: '8px' }}>Not Null</span>
          </label>
        </div>
      </div>
    </form>
    {showFormulaModal && createPortal(
      <div style={{ position: 'fixed', inset: 0, zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <FormulaCreationModal
          show={showFormulaModal}
          onClose={() => setShowFormulaModal(false)}
          groupCode={formData.groupCode}
          subGroupCode={formData.subGroupCode}
          initialFormula={formData.formula}
          onSave={(formulaStr) => {
            setFormData(prev => ({ ...prev, formula: formulaStr || '' }));
            setShowFormulaModal(false);
          }}
        />
      </div>,
      document.body
    )}
    {showAssociatedModal && createPortal(
      <div style={{ position: 'fixed', inset: 0, zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <AssociatedCreationModal
          show={showAssociatedModal}
          onClose={() => setShowAssociatedModal(false)}
          groupCode={formData.groupCode}
          subGroupCode={formData.subGroupCode}
          excludeFieldCode={field?.code || field?.Code || ''}
          existingFields={fields}
          projectCode={projectCode}
          initialRefColumn={formData.refColumn}
          onSave={(jsonStr) => {
            setFormData((prev) => ({ ...prev, refColumn: jsonStr || '' }));
            setShowAssociatedModal(false);
          }}
        />
      </div>,
      document.body
    )}
  </>
  );
};

const AssociatedCreationModal = ({
  show,
  onClose,
  groupCode,
  subGroupCode,
  excludeFieldCode,
  existingFields = [],
  projectCode,
  initialRefColumn = '',
  onSave,
}) => {
  const [attributes, setAttributes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stages, setStages] = useState([]);
  const [driver, setDriver] = useState(null);
  const [branches, setBranches] = useState({});
  const [branchStageCode, setBranchStageCode] = useState('');
  const [driverDropReject, setDriverDropReject] = useState('');

  const ex = String(excludeFieldCode || '');
  const usedDriverFieldCodes = useMemo(() => {
    const map = new Map();
    (Array.isArray(existingFields) ? existingFields : []).forEach((f) => {
      const code = String(f?.code ?? f?.Code ?? '').trim();
      if (!code || code === ex) return;
      const type = String(f?.type ?? f?.Type ?? '').trim().toLowerCase();
      if (type !== 'associated') return;
      const rawRef = String(f?.refColumn ?? f?.RefColumn ?? '').trim();
      if (!rawRef) return;
      try {
        const parsed = JSON.parse(rawRef);
        const driverCode = String(parsed?.driverFieldCode ?? '').trim();
        if (!driverCode) return;
        const ownerDesc = String(f?.description ?? f?.Description ?? code).trim() || code;
        map.set(driverCode, ownerDesc);
      } catch {
        /* ignore malformed refColumn */
      }
    });
    return map;
  }, [existingFields, ex]);

  const getDuplicateDriverMessage = (driverCode) => {
    const code = String(driverCode || '').trim();
    if (!code) return '';
    if (!usedDriverFieldCodes.has(code)) return '';
    const owner = usedDriverFieldCodes.get(code) || code;
    return `This question is already used as a control field in another association (${owner}).`;
  };
  const firstReviewStageCode = useMemo(
    () => resolveFirstReviewStageCode(stages),
    [stages]
  );

  useEffect(() => {
    if (!show || !groupCode) return;
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams();
    if (groupCode) params.set('groupCode', groupCode);
    if (subGroupCode) params.set('subGroupCode', subGroupCode);
    api.get(`/crftemplate/fields?${params.toString()}`)
      .then((res) => {
        if (!cancelled && Array.isArray(res.data)) {
          setAttributes(res.data.filter((it) => String(it.code ?? it.Code ?? '') !== ex));
        }
      })
      .catch(() => { if (!cancelled) setAttributes([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [show, groupCode, subGroupCode, ex]);

  useEffect(() => {
    if (!show) return;
    stageService.getStagesForCrFAssociation(projectCode || undefined)
      .then((list) => { if (Array.isArray(list)) setStages(list); })
      .catch(() => setStages([]));
  }, [show, projectCode]);

  useEffect(() => {
    if (!show) return;
    const raw = (initialRefColumn || '').trim();
    if (!raw) {
      setDriver(null);
      setBranches({});
      setBranchStageCode('');
      setDriverDropReject('');
      return;
    }
    try {
      const j = JSON.parse(raw);
      setDriver({
        code: j.driverFieldCode || '',
        type: j.driverType || '',
        values: j.driverValues != null ? String(j.driverValues) : '',
        description: j.driverDescription || j.driverFieldCode || '',
      });
      setBranches(j.branches && typeof j.branches === 'object' ? j.branches : {});
      const legacy = j.stageCode != null && j.stageCode !== '' ? String(j.stageCode) : '';
      const hasBranchKey = Object.prototype.hasOwnProperty.call(j, 'branchStageCode');
      setBranchStageCode(
        hasBranchKey
          ? (j.branchStageCode != null && j.branchStageCode !== '' ? String(j.branchStageCode) : '')
          : legacy
      );
      setDriverDropReject('');
    } catch {
      setDriver(null);
      setBranches({});
      setBranchStageCode('');
      setDriverDropReject('');
    }
  }, [show, initialRefColumn]);

  useEffect(() => {
    if (!show) return;
    if (!firstReviewStageCode) return;
    setBranchStageCode(firstReviewStageCode);
  }, [show, firstReviewStageCode]);

  const onDragStartAttr = (e, item) => {
    const code = String(item.code ?? item.Code ?? '');
    const payload = {
      code,
      type: String(item.type ?? item.Type ?? ''),
      values: String(item.values ?? item.Values ?? ''),
      description: String(item.description ?? item.Description ?? code),
    };
    e.dataTransfer.setData('application/json', JSON.stringify(payload));
    e.dataTransfer.effectAllowed = 'copy';
  };

  const onDropDriver = (e) => {
    e.preventDefault();
    setDriverDropReject('');
    try {
      const raw = e.dataTransfer.getData('application/json');
      if (!raw) return;
      const p = JSON.parse(raw);
      const code = String(p.code || '');
      if (!code || code === ex) return;
      const dupMsg = getDuplicateDriverMessage(code);
      if (dupMsg) {
        setDriverDropReject(dupMsg);
        return;
      }
      if (!isDriverAllowedFieldType(p.type)) {
        setDriverDropReject('Only a Radio field can be used as the control field.');
        return;
      }
      setDriver({
        code,
        type: p.type || '',
        values: p.values || '',
        description: p.description || code,
      });
      const keys = getBranchKeysForDriver(p.type, p.values);
      setBranches(normalizeBranchMap(keys, {}));
    } catch {
      /* ignore */
    }
  };

  const onDropBranch = (branchKey, e) => {
    e.preventDefault();
    const keysForDrop = driver ? getBranchKeysForDriver(driver.type, driver.values) : [];
    if (!isAssociatedRadioBranchDroppable(driver?.type, branchKey, keysForDrop)) return;
    try {
      const raw = e.dataTransfer.getData('application/json');
      if (!raw) return;
      const p = JSON.parse(raw);
      const childCode = String(p.code || '');
      if (!childCode || childCode === ex || (driver && childCode === driver.code)) return;
      setBranches((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((k) => {
          next[k] = (next[k] || []).filter((c) => c !== childCode);
        });
        if (!next[branchKey]) next[branchKey] = [];
        if (!next[branchKey].includes(childCode)) next[branchKey] = [...next[branchKey], childCode];
        return next;
      });
    } catch {
      /* ignore */
    }
  };

  const removeChild = (branchKey, childCode) => {
    setBranches((prev) => ({
      ...prev,
      [branchKey]: (prev[branchKey] || []).filter((c) => c !== childCode),
    }));
  };

  const clearDriver = () => {
    setDriver(null);
    setBranches({});
    setDriverDropReject('');
  };

  const parseStageSelect = (s) => {
    if (s === '' || s == null) return null;
    const n = parseInt(String(s), 10);
    return Number.isNaN(n) ? null : n;
  };

  const handleSave = () => {
    if (!driver?.code) return;
    const dupMsg = getDuplicateDriverMessage(driver.code);
    if (dupMsg) {
      setDriverDropReject(dupMsg);
      return;
    }
    if (!isDriverAllowedFieldType(driver.type)) {
      setDriverDropReject('Only a Radio field can be used as the control field.');
      return;
    }
    const payload = {
      driverFieldCode: driver.code,
      driverType: driver.type,
      driverValues: driver.values || '',
      driverDescription: driver.description || '',
      branches: branches || {},
      branchStageCode: parseStageSelect(firstReviewStageCode || branchStageCode),
    };
    onSave(JSON.stringify(payload));
  };

  const branchKeys = driver ? getBranchKeysForDriver(driver.type, driver.values) : [];
  const hasYesRadioBranch =
    !!driver &&
    isDriverAllowedFieldType(driver.type) &&
    branchKeys.some((k) => String(k || '').trim().toLowerCase() === 'yes');
  const labelForBranch = (key) => {
    if (key === '__default__') return 'Default';
    if (key === '__checked__') return 'Checked';
    if (key === '__unchecked__') return 'Unchecked';
    return key;
  };

  if (!show) return null;
  return (
    <div className="formula-creation-modal-backdrop" onClick={onClose} role="presentation">
      <div className="formula-creation-modal-dialog formula-creation-modal associated-creation-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content border-0 shadow-none">
          <div className="modal-header">
            <h5 className="modal-title">{initialRefColumn && String(initialRefColumn).trim() ? 'Edit Association' : 'Create Association'}</h5>
            <button type="button" className="formula-modal-close-btn" onClick={onClose} aria-label="Close">
              <svg stroke="currentColor" fill="none" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round" height="24" width="24" xmlns="http://www.w3.org/2000/svg">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          <div className="modal-body">
            <div className="formula-modal-row associated-modal-row">
              <div className="formula-modal-section associated-modal-attributes-col">
                <h6 className="formula-modal-section-title">Attributes</h6>
                {loading ? (
                  <p className="formula-attributes-empty">Loading...</p>
                ) : (
                  <div className="formula-attributes-box associated-attributes-scroll">
                    <div className="formula-attributes-list associated-attributes-list-vertical">
                      {attributes.map((item, idx) => {
                        const code = item.code || item.Code || idx;
                        return (
                          <button
                            key={code}
                            type="button"
                            draggable
                            className="formula-attr-btn"
                            title={item.description || item.Description || item.code}
                            onDragStart={(e) => onDragStartAttr(e, item)}
                          >
                            {item.description || item.Description || item.code}
                          </button>
                        );
                      })}
                    </div>
                    {attributes.length === 0 && !loading && (
                      <span className="formula-attributes-empty">Select Group and Sub Group first.</span>
                    )}
                  </div>
                )}
                <p className="associated-modal-hint small text-muted mt-2 mb-0">Drag an attribute into the control field or branch areas on the right.</p>
              </div>
              <div className="formula-modal-section associated-modal-config-col">
                <h6 className="formula-modal-section-title">Control field</h6>
                <div
                  className={`associated-dropzone associated-dropzone-driver ${driver ? 'associated-dropzone-filled' : ''}`}
                  onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; }}
                  onDrop={onDropDriver}
                >
                  {driver ? (
                    <div className="associated-driver-chip">
                      <span>{driver.description || driver.code}</span>
                      <button type="button" className="associated-chip-remove" onClick={clearDriver} aria-label="Remove control field">×</button>
                    </div>
                  ) : (
                    <span className="associated-dropzone-placeholder">Drop one Radio field here as the control field</span>
                  )}
                </div>
                {driverDropReject ? (
                  <div className="associated-driver-reject small text-danger mt-1" role="alert">{driverDropReject}</div>
                ) : null}

                {driver && (
                  <>
                    <h6 className="formula-modal-section-title mt-3">Answer branches</h6>
                    {branchKeys.map((bk) => (
                      <div key={bk} className="associated-branch-block">
                        <div className="associated-branch-label">{labelForBranch(bk)}</div>
                        {hasYesRadioBranch && !isAssociatedRadioBranchDroppable(driver.type, bk, branchKeys) ? (
                          <div className="small text-muted mb-1">
                            This branch does not allow dropped attributes. Only the Yes branch can include associated questions.
                          </div>
                        ) : null}
                        <div
                          className="associated-dropzone associated-dropzone-branch"
                          onDragOver={(e) => {
                            if (!isAssociatedRadioBranchDroppable(driver?.type, bk, branchKeys)) {
                              e.dataTransfer.dropEffect = 'none';
                              return;
                            }
                            e.preventDefault();
                            e.dataTransfer.dropEffect = 'copy';
                          }}
                          onDrop={(e) => onDropBranch(bk, e)}
                          style={
                            hasYesRadioBranch && !isAssociatedRadioBranchDroppable(driver?.type, bk, branchKeys)
                              ? { background: '#f9fafb', opacity: 0.75, cursor: 'not-allowed' }
                              : undefined
                          }
                        >
                          <div className="associated-branch-chips">
                            {(branches[bk] || []).map((cc) => (
                              <span key={cc} className="associated-child-chip">
                                {attributes.find((a) => String(a.code ?? a.Code) === cc)?.description
                                  || attributes.find((a) => String(a.code ?? a.Code) === cc)?.Description
                                  || cc}
                                <button type="button" className="associated-chip-remove" onClick={() => removeChild(bk, cc)} aria-label="Remove">×</button>
                              </span>
                            ))}
                            {(branches[bk] || []).length === 0 && (
                              <span className="associated-dropzone-placeholder small">Drop attributes for this branch</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}

                <div className="associated-stage-wrap mt-3">
                  <label className="formula-field-label" htmlFor="associated-branch-stage-select">Stage (study): associated answers</label>
                  <select
                    id="associated-branch-stage-select"
                    className="formula-operator-select w-100"
                    value={firstReviewStageCode || branchStageCode}
                    disabled
                  >
                    <option value="">Any stage</option>
                    {stages.map((s) => {
                      const sc = s.stageCode ?? s.StageCode;
                      const id = s.id ?? s.Id ?? String(sc);
                      const lab = s.label ?? s.Label ?? s.stageName ?? s.StageName ?? String(sc);
                      return (
                        <option key={id} value={String(sc)}>{lab}</option>
                      );
                    })}
                  </select>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-primary" onClick={handleSave} disabled={!driver?.code}>
              Save
            </button>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Formula Creation Modal - same as Ramp: build formula from attributes (field codes) + operators
const FormulaCreationModal = ({ show, onClose, groupCode, subGroupCode, initialFormula = '', onSave }) => {
  const [attributes, setAttributes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formulaParts, setFormulaParts] = useState([]); // array of { type: 'code'|'op', value }
  const [operator, setOperator] = useState('');
  const [decimalNo, setDecimalNo] = useState('');
  const operators = ['+', '-', '*', '/', '(', ')', '^'];

  useEffect(() => {
    if (!show || !groupCode) return;
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams();
    if (groupCode) params.set('groupCode', groupCode);
    if (subGroupCode) params.set('subGroupCode', subGroupCode);
    api.get(`/crftemplate/fields?${params.toString()}`)
      .then((res) => {
        if (!cancelled && Array.isArray(res.data)) setAttributes(res.data);
      })
      .catch(() => { if (!cancelled) setAttributes([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [show, groupCode, subGroupCode]);

  useEffect(() => {
    if (!show) return;
    if (initialFormula && typeof initialFormula === 'string' && initialFormula.trim()) {
      const parts = initialFormula.trim().split(/\s+/).map(p => {
        if (operators.includes(p)) return { type: 'op', value: p };
        return { type: 'code', value: p };
      });
      setFormulaParts(parts);
    } else {
      setFormulaParts([]);
    }
    setOperator('');
    setDecimalNo('');
  }, [show, initialFormula]);

  const addAttribute = (code, desc) => {
    const last = formulaParts[formulaParts.length - 1];
    if (last?.type === 'code' && !operators.includes(last?.value)) {
      return; // require operator between attributes
    }
    setFormulaParts(prev => [...prev, { type: 'code', value: code }]);
  };

  const addOperator = (op) => {
    if (!op) return;
    setFormulaParts(prev => [...prev, { type: 'op', value: op }]);
    setOperator('');
  };

  const addDigit = (d) => {
    setFormulaParts(prev => [...prev, { type: 'code', value: String(d) }]);
  };

  const removePart = (index) => {
    setFormulaParts(prev => prev.filter((_, i) => i !== index));
  };

  const clearAll = () => setFormulaParts([]);

  const getFormulaString = () => formulaParts.map(p => p.value).join(' ');

  const handleSave = () => {
    onSave(getFormulaString());
  };

  if (!show) return null;
  return (
    <div className="formula-creation-modal-backdrop">
      <div className="formula-creation-modal-dialog formula-creation-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content border-0 shadow-none">
          <div className="modal-header">
            <h5 className="modal-title">{initialFormula && initialFormula.trim() ? 'Edit Formula' : 'Create Formula'}</h5>
            <button type="button" className="formula-modal-close-btn" onClick={onClose} aria-label="Close">
              <svg stroke="currentColor" fill="none" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round" height="24" width="24" xmlns="http://www.w3.org/2000/svg">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          <div className="modal-body">
            <div className="formula-modal-row">
              <div className="formula-modal-section">
                <h6 className="formula-modal-section-title">Attributes</h6>
                {loading ? (
                  <p className="formula-attributes-empty">Loading...</p>
                ) : (
                  <div className="formula-attributes-box">
                    <div className="formula-attributes-list">
                      {attributes.map((item, idx) => (
                        <button
                          key={item.code || item.id || idx}
                          type="button"
                          className="formula-attr-btn"
                          title={item.description || item.Description || item.code}
                          onClick={() => addAttribute(item.code || item.id, item.description || item.Description)}
                        >
                          {item.description || item.Description || item.code}
                        </button>
                      ))}
                    </div>
                    {attributes.length === 0 && !loading && (
                      <span className="formula-attributes-empty">Select Group and Sub Group first.</span>
                    )}
                  </div>
                )}
              </div>
              <div className="formula-modal-section">
                <h6 className="formula-modal-section-title">Formula Builder</h6>
                <div className="formula-builder-box">
                  {formulaParts.map((part, idx) => (
                    <span key={idx} className="formula-part-badge">
                      {part.value}
                      <button
                        type="button"
                        className="formula-part-remove"
                        onClick={() => removePart(idx)}
                        aria-label="Remove"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                <div className="formula-builder-actions">
                  <button type="button" className="formula-clear-btn" onClick={clearAll}>
                    Clear All
                  </button>
                </div>
                <div className="formula-field-group">
                  <label className="formula-field-label">Operator</label>
                  <select
                    className="formula-operator-select"
                    value={operator}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v) {
                        addOperator(v);
                        setOperator('');
                      }
                    }}
                  >
                    <option value="">Select</option>
                    {operators.map(op => (
                      <option key={op} value={op}>{op}</option>
                    ))}
                  </select>
                </div>
                <div className="formula-field-group">
                  <label className="formula-field-label">Digits</label>
                  <div className="formula-digits-wrap">
                    {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map(d => (
                      <button
                        key={d}
                        type="button"
                        className="formula-digit-btn"
                        onClick={() => addDigit(d)}
                      >
                        {d}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="formula-field-group">
                  <label className="formula-field-label">Decimal No</label>
                  <input
                    type="number"
                    className="formula-decimal-input"
                    value={decimalNo}
                    onChange={(e) => setDecimalNo(e.target.value)}
                    min={0}
                    aria-label="Decimal places"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-primary" onClick={handleSave}>
              Save
            </button>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// CRF Template Mapping Form Component
const CRFTemplateMappingForm = ({ mappingForm, mappingFormErrors = {}, projects, onMappingChange, loading, projectDisabled = false }) => {
  const visitTypes = [
    { value: 'SSV', label: 'Site Selection Visit' },
    { value: 'SIV', label: 'Site Initiation Visit' },
    { value: 'SMV', label: 'Site Monitoring Visit' },
    { value: 'SCV', label: 'Site Close-Out Visit' },
  ];

  const projectDisplay = mappingForm.projectCode
    ? (projects.find(p => (p.projectCode || '') === mappingForm.projectCode)?.projectName) || mappingForm.projectCode
    : '';

  return (
    <form className="crf-template-mapping-form">
      <div className="form-group">
        <label className="form-label">Project <span className="text-danger">*</span></label>
        {projectDisabled ? (
          <input
            type="text"
            className="form-input"
            value={projectDisplay}
            readOnly
            disabled
            style={{ backgroundColor: 'var(--input-disabled-bg, #e9ecef)', cursor: 'not-allowed' }}
            aria-label="Project (from header)"
          />
        ) : (
          <select
            name="projectCode"
            className={`form-select ${mappingFormErrors.projectCode ? 'is-invalid' : ''}`}
            value={mappingForm.projectCode}
            onChange={(e) => onMappingChange('projectCode', e.target.value)}
            required
            disabled={loading}
          >
            <option value="">Select Project</option>
            {projects.map(project => (
              <option key={project.projectCode} value={project.projectCode}>
                {project.projectName || project.projectCode}
              </option>
            ))}
          </select>
        )}
        {mappingFormErrors.projectCode && <span className="text-danger small d-block">{mappingFormErrors.projectCode}</span>}
      </div>

      <div className="form-group">
        <label className="form-label">Visit Type <span className="text-danger">*</span></label>
        <select
          name="visitType"
          className={`form-select ${mappingFormErrors.visitType ? 'is-invalid' : ''}`}
          value={mappingForm.visitType}
          onChange={(e) => onMappingChange('visitType', e.target.value)}
          required
          disabled={loading}
        >
          <option value="">Select Visit Type</option>
          {visitTypes.map(visitType => (
            <option key={visitType.value} value={visitType.value}>
              {visitType.label}
            </option>
          ))}
        </select>
        {mappingFormErrors.visitType && <span className="text-danger small d-block">{mappingFormErrors.visitType}</span>}
      </div>
    </form>
  );
};

// CRF Template Edit Form Component
const CRFTemplateEditForm = ({ template }) => {
  // Handle both camelCase and PascalCase property names
  const [formData, setFormData] = useState({
    templateName: '',
    templateId: '',
    version: '1.0',
    status: 'Active',
    description: '',
  });

  // Update form data when template prop changes
  useEffect(() => {
    if (template) {
      const isActive = template?.isActive !== false && template?.IsActive !== false;
      setFormData({
        templateName: template?.templateName || template?.TemplateName || template?.name || '',
        templateId: template?.templateId || template?.TemplateId || template?.id || template?.templateHeaderNo || '',
        version: template?.version || template?.Version || '1.0',
        status: isActive ? 'Active' : 'Inactive',
        description: template?.description ?? template?.Description ?? '',
      });
    } else {
      setFormData({
        templateName: '',
        templateId: '',
        version: '1.0',
        status: 'Active',
        description: '',
      });
    }
  }, [template]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <form className="crf-template-edit-form" id="crf-template-edit-form">
      {/* Hidden inputs for templateId and version (version not editable in UI) */}
      <input type="hidden" name="templateId" value={formData.templateId} />
      <input type="hidden" name="version" value={formData.version} />
      
      <div className="form-group">
        <label className="form-label">Template Name <span className="text-danger">*</span></label>
        <input
          type="text"
          name="templateName"
          className="form-input"
          value={formData.templateName}
          onChange={handleChange}
          required
          placeholder="Enter Template Name"
        />
      </div>

      <div className="form-group">
        <label className="form-label">Active <span className="text-danger">*</span></label>
        <select
          name="status"
          className="form-select"
          value={formData.status}
          onChange={handleChange}
          required
        >
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>
      </div>

      <div className="form-group">
        <label className="form-label">Description</label>
        <textarea
          name="description"
          className="form-input"
          value={formData.description}
          onChange={handleChange}
          rows="3"
          placeholder="Enter description"
        />
      </div>
    </form>
  );
};

// CRF Template Fields Edit Form Component
const CRFTemplateFieldsEditForm = ({ 
  availableFields, 
  selectedFields, 
  onAddField, 
  onRemoveField, 
  onReorderFields,
  groups,
  subGroups,
  selectedGroup,
  selectedSubGroup,
  onGroupChange,
  onSubGroupChange,
  filterErrors = {},
  loading 
}) => {
  const [dragOverZone, setDragOverZone] = useState(null);
  const dragPayloadRef = useRef(null);

  const getDropData = (e) => {
    try {
      const raw = e.dataTransfer.getData('application/json');
      if (raw) return JSON.parse(raw);
      if (dragPayloadRef.current) return dragPayloadRef.current;
    } catch (_) {}
    return dragPayloadRef.current;
  };

  const clearDragPayload = () => { dragPayloadRef.current = null; };

  // Filter sub groups by selected group (active only for template design)
  const filteredSubGroups = subGroups.filter(sg => 
    (sg.active !== false) && (sg.groupCode || sg.GroupCode) === selectedGroup
  );

  return (
    <div className="crf-template-fields-edit">
      {/* Filter Section */}
      <div className="grid-2" style={{ marginBottom: '1.5rem' }}>
        <div className="form-group">
          <label className="form-label">Filter by Report Group <span className="text-danger">*</span></label>
          <select
            className={`form-select ${filterErrors.selectedGroup ? 'is-invalid' : ''}`}
            value={selectedGroup}
            onChange={(e) => onGroupChange(e.target.value)}
            disabled={loading}
          >
            <option value="">Select Report Group</option>
            {groups.filter(g => g.active !== false).map(group => (
              <option key={group.id || group.code} value={group.code || group.Code}>
                {group.description || group.Description}
              </option>
            ))}
          </select>
          {filterErrors.selectedGroup && <span className="text-danger small d-block">{filterErrors.selectedGroup}</span>}
        </div>
        <div className="form-group">
          <label className="form-label">Filter by Report Sub Group <span className="text-danger">*</span></label>
          <select
            className={`form-select ${filterErrors.selectedSubGroup ? 'is-invalid' : ''}`}
            value={selectedSubGroup}
            onChange={(e) => onSubGroupChange(e.target.value)}
            disabled={loading || !selectedGroup}
          >
            <option value="">Select Report Sub Group</option>
            {filteredSubGroups.map(subGroup => (
              <option key={subGroup.id || subGroup.code} value={subGroup.code || subGroup.Code}>
                {subGroup.description || subGroup.Description}
              </option>
            ))}
          </select>
          {filterErrors.selectedSubGroup && <span className="text-danger small d-block">{filterErrors.selectedSubGroup}</span>}
        </div>
      </div>

      <div className="crf-design-container">
        <div className="crf-available-box">
          <div className="crf-box-header available">
            <span>Available Fields</span>
            <i className="fas fa-list"></i>
          </div>
          <div
            className={`crf-box-content ${dragOverZone === 'available' ? 'crf-drop-over' : ''}`}
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = 'move'; setDragOverZone('available'); }}
            onDragLeave={() => setDragOverZone(null)}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setDragOverZone(null);
              const data = getDropData(e);
              if (data?.source === 'selected' && data?.field) {
                onRemoveField(data.field.id || data.field.code);
              }
              clearDragPayload();
            }}
          >
            {availableFields.length === 0 ? (
              <div className="crf-empty-state">No additional fields available</div>
            ) : (
              availableFields.map(field => (
                <div
                  key={field.id || field.code}
                  className="crf-field-item"
                  draggable
                  onDragStart={(e) => {
                    if (e.target.closest('button')) { e.preventDefault(); return; }
                    const payload = { field, source: 'available' };
                    e.dataTransfer.setData('application/json', JSON.stringify(payload));
                    e.dataTransfer.effectAllowed = 'move';
                    dragPayloadRef.current = payload;
                  }}
                  onDragEnd={clearDragPayload}
                >
                  <i className="fas fa-file-alt"></i>
                  <span>{field.description || field.Description}</span>
                  <button 
                    className="btn btn-sm btn-primary" 
                    onClick={() => onAddField(field)}
                    disabled={loading}
                  >
                    Add →
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="crf-selected-box">
          <div className="crf-box-header selected">
            <span>Template Fields ({selectedFields.length})</span>
            <i className="fas fa-check-circle"></i>
          </div>
          <div
            className={`crf-box-content ${dragOverZone === 'selected' ? 'crf-drop-over' : ''}`}
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = 'move'; setDragOverZone('selected'); }}
            onDragLeave={() => setDragOverZone(null)}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setDragOverZone(null);
              const data = getDropData(e);
              if (data?.source === 'available' && data?.field) {
                onAddField(data.field);
              } else if (data?.source === 'selected' && data?.field && onReorderFields) {
                onReorderFields(data.field.id || data.field.code, null);
              }
              clearDragPayload();
            }}
          >
            {selectedFields.length === 0 ? (
              <div className="crf-empty-state">No fields in template. Add fields from available list.</div>
            ) : (
              selectedFields.map(field => (
                <div
                  key={field.id || field.code}
                  className="crf-field-item selected"
                  draggable
                  onDragStart={(e) => {
                    if (e.target.closest('button')) { e.preventDefault(); return; }
                    const payload = { field, source: 'selected' };
                    e.dataTransfer.setData('application/json', JSON.stringify(payload));
                    e.dataTransfer.effectAllowed = 'move';
                    dragPayloadRef.current = payload;
                  }}
                  onDragEnd={() => { setDragOverZone(null); clearDragPayload(); }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.dataTransfer.dropEffect = 'move';
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDragOverZone(null);
                    if (!onReorderFields) return;
                    const data = getDropData(e);
                    if (data?.source === 'selected' && data?.field) {
                      const dropId = field.id || field.code;
                      const draggedId = data.field.id || data.field.code;
                      if (dropId !== draggedId) {
                        onReorderFields(draggedId, dropId);
                      }
                    }
                    clearDragPayload();
                  }}
                >
                  <i className="fas fa-file-alt"></i>
                  <span>{field.description || field.Description}</span>
                  <button 
                    className="btn btn-sm btn-danger" 
                    onClick={() => onRemoveField(field.id || field.code)}
                    disabled={loading}
                  >
                    Remove
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CRFManagement;
